import ReactDOM from "react-dom/client";
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect, forwardRef, useImperativeHandle } from "react";
import { NavigateOptions, To, useSearchParams } from "react-router-dom";
import styles from "./Map.module.css";
import { MarkerHighlightManager } from "./MarkerHighlightManager";

import {
    createBaseLayer,
    addPointStyle,
    fitToExtent,
    getLayer,
    getLayerGroup,
    getProjection,
    LayerName,
    createVectorLayer,
    createClusterLayer,
    clusterStyleFunction,
    createLayerGroup,
    getInteraction,
    removeInteractions,
    removeOverlays,
    revealedStyleFunction,
    selectedClusterStyleFunction,
    getInteractions,
    getSharedStyle,
    createSlopeLayer,
    disposeOlMap,
} from "../../module/map/olmap";
import {
    accessibilityCondition,
    accessibilityPolygonStyle,
    accessibilityRawPolygonStyle,
    accessibilityMarkerStyle,
    choroplethStyleFunction,
    chartStyleFunction,
    regionStyleFunction,
    createDirectionLayer,
    createRegionLayer,
    createChartRegionLayer,
    extentAnalysisPolyStyle,
    extentAnalysisCircleStyle,
    extentAnalysisStyle,
    extentAnalysisOutsideStyle,
    getClusterStyle,
    getRegionType,
    getUpperRegionCode,
    RegionType,
    startMeasure,
    stopDirection,
    stopAccessibility,
    stopMeasure,
    stopOverlapMap,
    directionStyleFunction,
    addIsochronePolygon,
    clearLayerByStage,
    drawRouteMultiLine,
    clearTooltip,
    getPolygonVerticesFromGeometry,
    createPolygonIntersectsFilterData,
    updateOverlayLabel,
    updateOverlayChartLabel,
    CalToolType,
    generateJenksLegend,
    classifyValue,
} from "../../module/map/analysis";

import DataProvider from "../../data_provider/DataProvider";
import { RegionAnalysisData, RegionAnalysisLegend } from "../../data_provider/gateways/AnalysisGateway";
import { ListSearchReqBody, MapInfoOptions } from "../../data_provider/gateways/MapGateway";

import { FeatureCollection, Feature, GeoJsonProperties, Polygon, MultiPolygon } from "geojson";
import { point as turfPoint, booleanPointInPolygon } from "@turf/turf";
import { intersect as turfIntersect } from "@turf/intersect";
import { feature, featureCollection, featureCollection as turfFeatureCollection } from "@turf/helpers";

import { Feature as OlFeature, Map as OlMap, Overlay, View, MapEvent } from "ol";
import { ScaleLine } from "ol/control";
import { Projection, toLonLat, transformExtent, fromLonLat } from "ol/proj";
import { GeoJSON } from "ol/format";
import { Layer, Heatmap as HeatmapLayer, Vector as VectorLayer, Group as LayerGroup } from "ol/layer";
import BaseLayer from "ol/layer/Base";
import { Vector as VectorSource, Cluster as ClusterSource } from "ol/source";
import { VectorSourceEvent } from "ol/source/Vector";
import { Draw, Modify, Translate, Select, Interaction } from "ol/interaction";
import { DrawEvent } from "ol/interaction/Draw";
import { TranslateEvent } from "ol/interaction/Translate";
import { Coordinate } from "ol/coordinate";
import { Geometry, SimpleGeometry, Point, Polygon as OlPolygon, MultiPolygon as OlMultiPolygon, Circle, LineString } from "ol/geom";
import Collection, { CollectionEvent } from "ol/Collection";
import { Fill, Stroke, Circle as CircleStyle } from "ol/style";
import Style, { StyleLike } from "ol/style/Style";
import { doubleClick, singleClick } from "ol/events/condition";
import ClusterLayer from "ol-ext/layer/AnimatedCluster";
import SelectCluster from "ol-ext/interaction/SelectCluster";
import Hover, { HoverEvent } from "ol-ext/interaction/Hover";
import { getDistance } from "ol/sphere";
import { circular as circularPolygon } from "ol/geom/Polygon";

import {
    PrimaryInfo,
    PlaceInfoSet,
    AllPlaceData,
    CategoryOptions,
    Color,
    FamousMarker,
    findCategoryOption,
    GET_FEATURE_UNIT,
    MapInfo,
    MetaData,
    path,
    PlaceCoordsList,
    Metadata,
} from "../common/Common";
import { AccessibilityState, MapToolType, ContentList } from "../../screens/MapScreen";
import { TransportMode } from "./sidesheet/analysis/TransportSelector";

import { transform } from "ol/proj";
import { FeatureLike } from "ol/Feature";
import { Stage } from "./sidesheet/analysis/DirectionAnalysis";

import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";

import Graph from "../common/Graph";
import { ToastData, ToastType } from "../common/Toast";
import { DropdownSelectorItems } from "../common/DropdownSelector";

import VariableRadiusHeatmap from "./VariableRadiusHeatmap";
import { POISearchOptions } from "./sidesheet/SearchPOI";
import { SearchOptions } from "../common/Search";
import { isReturnStatement } from "typescript";
import { WebViewMessageType } from "../../hooks/useWebViewBridge";

/**
 * 줌 레벨 설정
 * @interface
 * @prop {number} zoom 현재 줌 레벨
 * @prop {{@link Dispatch}<{@link SetStateAction}\<number\>>} setZoom 줌 레벨 설정 함수
 */
interface ZoomOptions {
    zoom: number;
    setZoom: Dispatch<SetStateAction<number>>;
}

/**
 * OL 지도 컴포넌트 Props 인터페이스
 *
 * @interface
 * @prop {number | string} selectedPoiIdx 선택된 장소 인덱스(number: 기존 장소 | string: 신규 추가 미리보기 장소)
 * @prop {Dispatch<SetStateAction<number | string>>} setSelectedPoiIdx 선택된 장소 인덱스 변경 함수
 * @prop {{@link CategoryOptions}[]} categories 지도 카데고리 목록
 * @prop {number} selectedCategory 선택된 카테고리 인덱스
 * @prop {number} activeIdx 선택된 지도 도구 인덱스
 * @prop {(idx: number) => void} 선택된 지도 도구 인덱스 변경 함수
 * @prop {Dispatch<SetStateAction<number[]>>} setExtentAnalysisXIDs
 * @prop {{@link ZoomOptions}} zoom 줌 설정
 * @prop {boolean} isGPSOn 현재 위치 사용 여부
 * @prop {boolean} isAddMode 신규 장소 추가 - 위치 등록 모드
 * @prop {number} mapPadding 지도 화면 좌측 패딩값
 * @prop {{@link PlaceCoordsList}[]} newPlaces (optional) 신규 장소 추가 좌표
 * @prop {Dispatch<SetStateAction<PlaceCoordsList[]>>} setNewPlace 신규 장소 변경 함수
 * @prop {number} blur 히트맵 블러 설정
 * @prop {Dispatch<SetStateAction<{@link RegionAnalysisLegend}[] | undefined>>} setLegend 범례 데이터 변경 함수
 * @prop {number} selectedMapIdx (optional) 선택된 지도 인덱스(0: 기존 지도)
 * @prop {{@link AccessibilityState}} accState? 접근성 분석 설정(분석 수단/지점 선택 완료 여부)
 * @prop {(vehicle?: {@link TransportMode}, ready?: boolean) => void} setAccState 접근성 분석 설정 변경 함수
 * @prop {{@link FamousMarker}[]} famousMarkers (optional) 인기 마커 스타일 목록
 * @prop {(to?: {@link To}, options?: {@link NavigateOptions}) => void} movePage 페이지 경로 이동 함수
 */
interface MapProps {
    // 25.07.11 | Kim Young Jun | isMobileView prop 추가
    isMobileView?: boolean;
    selectedPoiIdx: number | string;
    setSelectedPoiIdx: Dispatch<SetStateAction<number | string>>;

    categories: CategoryOptions[];
    // 25.09.02 | Kim Young Jun | selectedCategoryArray 기반으로 변경
    selectedCategoryArray: number[];

    activeIdx: number;
    setActiveIdx: (idx: number) => void;
    upadateSelectedCategory: (idx?: number) => void;

    setAnalysisXIDs: Dispatch<SetStateAction<number[] | undefined>>;

    zoom: ZoomOptions;
    isGPSOn: boolean;
    isAddMode: boolean;
    setIsGPSOn: Dispatch<SetStateAction<boolean>>;
    setIsGPSLoading: Dispatch<SetStateAction<boolean>>;

    mapPadding: number;

    newPlaces?: PlaceCoordsList[];
    setNewPlace: Dispatch<SetStateAction<PlaceCoordsList[]>>;

    allPlaceData: AllPlaceData;
    blur: number; // 히트맵

    clearByButton: number;
    setClearByButton: Dispatch<SetStateAction<number>>;

    analysisStage: number;
    setAnalysisStage: Dispatch<SetStateAction<number>>;

    isExtentAnalysis: boolean; // 범위분석 활성화 여부
    setIsExtentAnalysis: Dispatch<SetStateAction<boolean>>;

    setMapProj: Dispatch<SetStateAction<Projection | undefined>>;
    startPoint: number[] | undefined; // 시간별 이동거리 시작점
    setDirectionStartPoint: Dispatch<SetStateAction<number[] | undefined>>;
    isochronePolygon: OlPolygon | undefined;
    directionVehicle: string;

    setLegend?: Dispatch<SetStateAction<RegionAnalysisLegend[] | undefined>>;
    accState?: AccessibilityState;
    setAccState: (vehicle?: TransportMode, ready?: boolean) => void;

    famousMarkers?: FamousMarker[];
    movePage: (to?: To, options?: NavigateOptions) => void;

    // ScaleBar 컴포넌트를 위한 Map 객체 전달
    onMapReady?: (map: OlMap) => void;

    forceUpdate?: number; // 오버랩에서 클릭한 카테고리 인덱스가 이전 지도의 카테고리 인덱스와 동일할 경우 useEffect 트리거 발동을 위한 useState | 2025.04.14 | kmy

    resetAcc: boolean;
    setResetAcc: Dispatch<SetStateAction<boolean>>;

    setToast?: React.Dispatch<React.SetStateAction<ToastData | undefined>>;

    extentToolIdx?: number;
    analysisMetricsToolIdx?: number;
    dropdownSelectorItems?: DropdownSelectorItems[];
    setIsLoading?: Dispatch<SetStateAction<boolean>>;

    closeModal?: () => void;

    sideSheetCont?: ContentList;
    editPlaceStatus?: boolean;
    setEditPlaceStatus?: Dispatch<SetStateAction<boolean>>;
    prevSelectedIdxRef?: React.MutableRefObject<number | null>;
    newPlaceAddress: string;
    setNewPlaceAddress: Dispatch<SetStateAction<string>>;
    setIsOpenAgreeModal: Dispatch<SetStateAction<boolean>>;
    setRevGeoLoading: Dispatch<SetStateAction<boolean>>;
    setAgreeNoneAddress: Dispatch<SetStateAction<boolean>>;

    selectedDropdownField: string | null;

    searchOptions: POISearchOptions | undefined; // 검색 옵션
    setSearchOptions: Dispatch<SetStateAction<POISearchOptions | undefined>>;

    // 25.07.07 | Kim Young Jun | 분석 트리거 인덱스 설정
    analysisTriggerId?: number; // 동일 매트릭 선택 시에도 분석 동작하도록 상태 추가
    triggerAnalysis?: () => void; // 분석 트리거 함수
    prevAnalysisState?: boolean; // 이전 분석 도구 상태
    isAnalysisActive?: boolean; // 분석 활성화 여부
    isNotUseMarkerAnalysis?: boolean; // 마커를 사용하지 않는 분석 활성화 여부

    // 25.07.10 | Kim Young Jun | 단계분석, 현재 선택한 행정구역 정보 상태 추가
    isChoroplethAnalysis: boolean;
    setIsChoroplethAnalysis: Dispatch<SetStateAction<boolean>>;
    currentRegionCode: string | null;
    setCurrentRegionCode: Dispatch<SetStateAction<string | null>>;

    // 선택된 마커 해제를 위한 콜백들
    onClearSelectedMarkerOnly?: React.MutableRefObject<(() => void) | null>;
    onClearSelectedMarkerAndCloseSheet?: React.MutableRefObject<(() => void) | null>;

    // 25.07.11 | Kim Young Jun | 플로팅 시트 렌더링 상태 추가
    showFloatingSheet?: boolean;
    setShowFloatingSheet?: Dispatch<SetStateAction<boolean>>;

    // 25.07.17 | Kim Young Jun | 사이드 시트 닫힘 상태 추가
    isClosedSideSheet?: boolean;
    setIsClosedSideSheet?: Dispatch<SetStateAction<boolean>>;

    // 25.09.08 | Kim Young Jun | 오버랩 지도 전환 기능 추가
    mapInfos?: any[];
    selectedMapIdx?: number;
    setSelectedMapIdx?: Dispatch<SetStateAction<number>>;

    // 25.01.13 | Kim Young Jun | 경사도 분석 모듈화 추가
    slopeHook?: {
        isSlopeActive: boolean;
        initialize: (map: OlMap | null, startFn?: (map: OlMap) => void, stopFn?: (map: OlMap) => void) => void;
        startSlopeAnalysis: (map?: OlMap) => void;
        stopSlopeAnalysis: (map?: OlMap) => void;
        resetSlopeAnalysis: (map?: OlMap) => void;
    };

    agreeNoneAddress: boolean;
    minSlopeZoomLv: number;
    setLoadingMessage: Dispatch<SetStateAction<string>>;
    
    // WebView 메시지 전송 함수 (드래그 마커 좌표 전송용)
    onMarkerDragEnd?: (coords: number[]) => void;
    
    // 2026.01.15 | dslee | 지도 Zoom 변경 시 콜백 (WebView 전송용)
    onRegionChange?: (context: { regionCode: string; zoomDirection: 'upper' | 'lower' }) => void;
    postMessage?: (message: WebViewMessageType) => void; // 2026.01.159| dslee | 웹뷰 전송용
}

// WebView와의 상호작용을 위한 Map 컴포넌트 제어 인터페이스
export interface MapWebViewHandle {
    showDraggableMarker: () => void;
    hideDraggableMarker: () => void;
    refreshMap: () => void;
    centerToLocation: (latitude: number, longitude: number) => void;
}

/**
 * OL 지도 컴포넌트
 * @author mjkang
 * @since 2024-08-23 10:23:40
 * @param props {@link MapProps}
 * @returns Map
 */
const Map = forwardRef<MapWebViewHandle, MapProps>((props, ref) => {
    const { ModalComponent, openModal, closeModal } = useModal();

    // 25.09.02 | Kim Young Jun | selectedCategoryArray 기반 헬퍼 함수 추가
    const getCurrentSelectedCategory = (): number => {
        return props.selectedCategoryArray[props.selectedMapIdx!] ?? 0;
    };

    const getCurrentCategoryLabel = (): string | undefined => {
        const currentIdx = getCurrentSelectedCategory();
        if (currentIdx > 0 && currentIdx < props.categories.length) {
            const categoryItem = props.categories[currentIdx];
            if (categoryItem && categoryItem.label) {
                return categoryItem.label;
            }
        }
        return undefined;
    };

    const isCategoryAll = (): boolean => {
        const currentIdx = getCurrentSelectedCategory();
        return currentIdx === 0 || getCurrentCategoryLabel() === "전체";
    };

    // const resolutions = [768.75, 522.24, 261.12, 130.56, 65.28, 32.64, 16.32, 8.16, 4.08, 2.04, 1.02, 0.51, 0.255]; // EPSG:5179 기준
    // const resolutions = [ 512,    256,   128,   64,     32,   16,    8,    4,    2,    1,  0.5]; // EPSG:5179 기준
    // const resolutions = [1954.597389, 977.2986945, 488.64934725, 244.324673625, 122.1623368125, 61.08116840625, 30.540584203125, 15.2702921015625, 7.63514605078125, 3.817573025390625, 1.9087865126953125, 0.9543932563476563, 0.47719662817382813, 0.23859831408691406]; // EPSG:5179 기준
    const resolutions = [
        2048, // zoom 0
        1024, // zoom 1
        512, // zoom 2
        256, // zoom 3
        128, // zoom 4
        64, // zoom 5
        32, // zoom 6
        16, // zoom 7
        8, // zoom 8
        4, // zoom 9
        2, // zoom 10
        1, // zoom 11
        0.5, // zoom 12
        0.25, // zoom 13
        0.125, // zoom 14
    ];

    const extent = [90112, 1392896, 1600673, 2161664]; // EPSG:5179 기준
    // const extent = [90112 - (90112 * 0.35), 1392896 - (1392896 * 0.35), 1600673 * 1.35, 2161664 * 1.35]; // EPSG:5179 기준
    // const extent = [531371.84, 957856.66, 1663741.9, 2274021.31]; // EPSG:5179 기준
    // const extent = Projection.get( "EPSG:5179" ).getExtent();
    const graphOverlayRef = useRef<HTMLDivElement>(null); // 차트(그래프)를 담을 HTMLDivElement
    const graphOverlay = useRef<Overlay | null>(null); // 차트(그래프) 오버레이
    const graphRootRef = useRef<ReactDOM.Root | null>(null) as { current: ReactDOM.Root | null }; // graphOverlayRef.current에 React 컴포넌트를 렌더링 ReactDOM 객체
    const labelOverlay = useRef<Overlay | null>(null); // 피처 라벨 오버레이
    const selectedPoiIdxRef = useRef<number | string>(props.selectedPoiIdx);
    const analysisMetricRef = useRef(props.analysisMetricsToolIdx);

    const allPlaceDataRef = useRef(props.allPlaceData);

    const { zoom, setZoom } = props.zoom;
    const [params] = useSearchParams();
    const baseMapID = useMemo<string | null>(() => params.get("id"), [params]);
    const overlapMapIDs = useMemo<string[] | undefined>(() => params.get("overlap")?.split(","), [params]);
    const legendData = useMemo<{ [key: string]: RegionAnalysisLegend[] }>(() => {
        return {};
    }, []);

    const [metaData, setMetaData] = useState<MetaData | undefined>();
    const [isOnGraph, setIsOnGraph] = useState<boolean>(false); // 차트(그래프) 오버레이 활성화 여부

    // 분석에 사용되는 feature 속성 목록
    const [analysisFeatureProperties, setAnalysisFeatureProperties] = useState<{ [key: string]: any }>({});

    // 25.09.08 | Kim Young Jun | 최근 필터링된 결과를 저장 - 카테고리 검증용
    const lastFilteredResultsRef = useRef<any[]>([]);

    // 25.09.11 | Kim Young Jun | 최초 등록 중인 오버랩 지도 mapid들을 추적 - setOverlapFeatures 중복 호출 방지

    const registeringOverlapMapIds = useRef<Set<string>>(new Set());
    // 25.09.12 | Kim Young Jun | 지도 전환 중 하이라이트 복원을 위한 대기 정보
    const pendingHighlightRef = useRef<{ mapId: string; xid: number } | null>(null);

    // 25.09.12 | Kim Young Jun | 마커 직접 클릭인지 POI 리스트 클릭인지 구분하기 위한 플래그
    const isDirectMarkerClickRef = useRef<boolean>(false);

    // 25.09.12 | Kim YOung Jun | FloatingSideSheet 닫기로 인한 줌 변경인지 구분하기 위한 플래그
    const isClosingFloatingSideSheetRef = useRef<boolean>(false);

    // 지역크기에 따른 줌 resolution 범위 관리용 최근 선택한 지역 코드 | smyun | 250723
    const recentRegionCode = useRef(0);

    const getfeatureStatus = useRef<{ [page: number]: boolean }>(); // feature 데이터 로드 여부 저장
    const removeRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<HTMLDivElement>(null);
    const activeIdxRef = useRef(props.activeIdx);

    // 25.07.02 | Kim Young Jun | 하이라이팅 매니저 인스턴스 생성
    const highlightManager = useMemo(() => new MarkerHighlightManager(), []);

    // 이전에 선택된 Feature들을 저장하는 Ref
    // 새로운 Feature가 선택되었을 때 기존 Feature의 선택을 해제하기 위해 사용
    // const previousSelectedFeaturesRef = useRef<OlFeature<Geometry>[]>([]);

    // 장소 수정시 위치로 마커를 이동시키때 원복을 위한 위치
    const revertPositionRef = useRef<Coordinate | null>(null);
    const translateGeometry = useRef<Point | null>(null);

    // gsshin (2026.01.23) : OL Map dispose 중복 실행 방지용 플래그
    // - MapScreen 전환/리렌더/cleanup 타이밍에서 dispose가 2번 이상 호출되는 것을 방지
    const disposedRef = useRef(false);

    // @@ map 해상도 이슈:
    // 현상: 차트 분석에서 최상위 레이어(COUNTRY)로 레이어 변경이 되지않는 현상
    // 원인: 디바이스, 브라우저 해상도에 따라, map.getView()로 받아올 수 있는 해상도 값이 다름
    // 원인: 특히, zoomLevel이 0일때, 해상도 값이 COUNTRY 레이어의 해상도보다 작음
    // 해결: 지도를 최대로 축소했을 때, 2560, 1920, 1440 해상도 테스트 결과 zoom값 1.46 이하로 획득되는 것으로 확인되어 1.5 값 지정 부여
    const magicZoomLv = 1.5;

    const setZoomLv = useCallback(
        (e: MapEvent) => {
            const map = e.map;
            const current = map.getView().getZoom();
            if (typeof current === "number" && current !== zoom) {
                setZoom(current);
            }
        },
        [setZoom, zoom]
    );

    // 지도가 움직이는 도중에는 다른 줌 변경이 일어나지 않도록 변수 설정
    let syncing = false;

    // 원본
    const mapObj = useMemo(() => {
        if (!mapRef.current || !metaData) return;
        if (mapRef.current.innerHTML) mapRef.current.innerHTML = ""; // clean
        const mapProj = getProjection(metaData.mapType);
        const mapExtent = transformExtent(extent, "EPSG:5179", mapProj);

        const _mapObj = new OlMap({
            layers: [
                createBaseLayer(metaData.mapType),
                // createVectorLayer(LayerName.FEATURE),
                createClusterLayer(LayerName.CLUSTER),
                createVectorLayer(LayerName.ADD), // 신규 등록
                createVectorLayer(LayerName.EDIT), // 신규 등록
                createRegionLayer(LayerName.REGION), // 행정구역
                createRegionLayer(LayerName.CHOROPLETH), // 단계구분도
                createChartRegionLayer(LayerName.CHART), // 차트분석
                new ClusterLayer({
                    source: new VectorSource({}) as any,
                    animationDuration: 400,
                    properties: {
                        name: LayerName.BUBBLE, // 버블맵
                        zIndex: 2,
                    },
                    visible: false,
                }),
                new VariableRadiusHeatmap({
                    source: new VectorSource({}),
                    visible: false,
                    blur: 10,
                    opacity: 0.8, // 투명도
                    weight: (f: OlFeature<Point>) => f.get("weight") ?? 0.5,
                    properties: {
                        name: LayerName.HEAT, // 히트맵
                        zIndex: 2,
                    },
                }),
                // new HeatmapLayer({
                //     source: new VectorSource({}),
                //     visible: false,
                //     blur: 1000,
                //     radius: 8,
                //     opacity: 0.8, // 투명도
                //     weight: (f) => f.get("weight") ?? 0.5,
                //     properties: {
                //         name: LayerName.HEAT, // 히트맵
                //         zIndex: 2,
                //     },
                // }),
                createDirectionLayer(LayerName.DIRECTION), // 시간별 이동거리
                createLayerGroup(LayerName.OVERLAP), // 오버랩
                createVectorLayer(LayerName.ACCESSIBILITY), // 접근성
                createVectorLayer(LayerName.CALCULATE), // 계산도구 레이어
                createVectorLayer(LayerName.ExtentAnalysis), // 범위분석 레이어
            ],
            view: new View({
                projection: "EPSG:5179",
                resolutions: resolutions,
                extent: [90112, 1192896, 1990673, 2761664], // 한국 영역
                center: [953974, 1952035],
                // constrainResolution: true,
                zoom: 3,
            }),
            // view: new View({
            //     projection: mapProj,
            //     resolutions: resolutions,
            //     center: [(mapExtent[0] + mapExtent[2]) / 2, (mapExtent[1] + mapExtent[3]) / 2],// [14141320, 4506856.5],//
            //     zoom: 0,

            //     extent: mapExtent,
            //     minZoom: 0,
            //     maxZoom:5,
            //     constrainResolution: true,
            // }),

            pixelRatio: 1,

            controls: [],
        });

        _mapObj.setTarget(mapRef.current);

        _mapObj.on("movestart", () => {
            syncing = true;
        });
        _mapObj.on("moveend", () => {
            syncing = false;
        });
        _mapObj.on("moveend", setZoomLv);

        // Chrome 콘솔에서 접근 가능하도록 전역 window 객체에 할당
        (window as any).olMap = _mapObj;

        // Map 객체가 준비되었음을 부모 컴포넌트에 알림
        if (props.onMapReady) {
            props.onMapReady(_mapObj);
        }

        return _mapObj;
    }, [metaData, mapRef.current]);

    // mapProj 설정을 별도 useEffect로 분리 (렌더링 중 setState 방지)
    useEffect(() => {
        if (metaData && props.setMapProj) {
            const mapProj = new Projection({
                code: getProjection(metaData.mapType).getCode(),
                extent: [90112, 1192896, 1990673, 2761664],
            });
            props.setMapProj(mapProj);
        }
    }, [metaData, props.setMapProj]);

    // gsshin (2026.01.23) : useEffect(() => {}, [mapObject]) 에서 정리 작업 하도록 수정함
    //                       수정한 정리작업 동작 확인후 아래 주석 코드는 삭제 필요.
    // useEffect(() => {
    //     if (mapObj) {
    //         return () => {
    //             mapObj.setTarget(undefined);
    //         };
    //     }
    // }, [mapRef.current]);

    // FloatingSideSheet 닫기 함수
    const closeFloatingSideSheet = useCallback(() => {
        console.log("[closeFloatingSideSheet] 호출됨");

        // FloatingSideSheet 닫기
        if (props.setShowFloatingSheet) {
            props.setShowFloatingSheet(false);
        }

        // POI 선택 해제
        if (props.setSelectedPoiIdx) {
            props.setSelectedPoiIdx(0);
        }

        // 마커 하이라이트 해제 (FloatingSideSheet 닫기는 이미 위에서 처리됨)
        highlightManager.clearAllSelections(false);
        selectedPoiIdxRef.current = 0;

        const currentZoom = mapObj?.getView().getZoom() ?? 0;
        isClosingFloatingSideSheetRef.current = true;
        setZoom(currentZoom + 0.0001);
    }, [props.setShowFloatingSheet, props.setSelectedPoiIdx, highlightManager, mapObj]);

    // highlightManager에 FloatingSideSheet 닫기 콜백 등록
    useEffect(() => {
        highlightManager.setCloseFloatingSideSheetCallback(closeFloatingSideSheet);
    }, [highlightManager, closeFloatingSideSheet]);

    // 선택된 마커만 해제 (FloatingSideSheet는 유지)
    const clearSelectedMarkerOnly = useCallback(() => {
        highlightManager.clearAllSelections(false);
    }, [highlightManager]);

    // FloatingSideSheet 닫기
    const clearSelectedMarkerAndCloseSheet = useCallback(() => {
        highlightManager.clearAllSelections(true);
    }, [highlightManager, mapObj]);

    // clearSelectedMarker 함수들을 외부에서 접근할 수 있도록 props를 통해 연결
    useEffect(() => {
        if (props.onClearSelectedMarkerOnly && typeof props.onClearSelectedMarkerOnly === "object" && "current" in props.onClearSelectedMarkerOnly) {
            props.onClearSelectedMarkerOnly.current = clearSelectedMarkerOnly;
        }
    }, [props.onClearSelectedMarkerOnly, clearSelectedMarkerOnly]);

    useEffect(() => {
        if (
            props.onClearSelectedMarkerAndCloseSheet &&
            typeof props.onClearSelectedMarkerAndCloseSheet === "object" &&
            "current" in props.onClearSelectedMarkerAndCloseSheet
        ) {
            props.onClearSelectedMarkerAndCloseSheet.current = clearSelectedMarkerAndCloseSheet;
        }
    }, [props.onClearSelectedMarkerAndCloseSheet, clearSelectedMarkerAndCloseSheet]);

    useEffect(() => {
        if (!baseMapID) return;

        console.log("@@@@@@ metadata useEffect called reqMetadata: ", baseMapID);
        !metaData && reqMetadata(baseMapID).then((md) => md && setMetaData(md));
    }, [metaData]);

    useEffect(() => {
        if (!mapObj || !baseMapID) return;

        setRegionPolygons(mapObj); // choropleth, region 레이어 미리 생성

        mapObj.on("moveend", setZoomLv);

        if (overlapMapIDs) {
            console.log("@@@@@@ mapObj useEffect called startOverlapMap: ", overlapMapIDs[props.selectedMapIdx! - 1]);
            overlapMapIDs[props.selectedMapIdx! - 1] &&
                mapObj.once("loadend", () => startOverlapMap(mapObj, overlapMapIDs[props.selectedMapIdx! - 1]));
        } else {
            stopOverlapMap(mapObj);
        }

        return () => {
            mapObj && mapObj.un("moveend", setZoomLv);
        };
    }, [mapObj, overlapMapIDs]);

    useEffect(() => {
        if (props.selectedMapIdx == 0 || props.selectedMapIdx == undefined) {
            // 선택된 지도의 인덱스가 0일때만 실행
            // if (props.categories.length === 1 || !mapObj || !baseMapID || !isCategoryAll()) return;
            if (!mapObj || !baseMapID || !isCategoryAll()) return;

            console.log("@@@@@ mapObj called reqMapData: ", baseMapID);
            reqMapData(baseMapID, mapObj, "map");
        }
    }, [mapObj]);

    // gsshin (2026.01.23) : OL Map dispose 전용
    // - 화면 unmount 또는 mapObj 교체 시점에 OL 리소스를 정리하여 메모리 점유를 줄임
    // - 다른 useEffect들에서는 dispose 호출하지 말고, 이 effect에서만 일괄 정리
    useEffect(() => {
        if (!mapObj) return;

        // gsshin (2026.01.23) : mapObj가 새로 세팅되면 dispose 플래그 초기화
	    disposedRef.current = false;

        return () => {  
            // gsshin (2026.01.23) : 중복 dispose 방지
            if (disposedRef.current) return;
            
            disposedRef.current = true;

            // gsshin (2026.01.23) : OL Map 객체 정리 (overlays/interactions/controls/layers/source/target detach)
            disposeOlMap(mapObj);
        }
    }, [mapObj]);

    useEffect(() => {
        if (!mapObj) return;

        console.log(`[useEffect selectedPoiIdx] 시작: current=${selectedPoiIdxRef.current}, new=${props.selectedPoiIdx}`);

        if (typeof props.selectedPoiIdx === "string") {
            selectNewFeature(mapObj, props.selectedPoiIdx); // 신규 장소 미리보기
            return;
        }

        // 중복 방지 로직 개선: 유효하지 않은 값이거나 0일 때만 제외
        // if (!props.selectedPoiIdx /*|| props.selectedPoiIdx === 0*/) {
        //     console.log("[useEffect selectedPoiIdx] 유효하지 않은 selectedPoiIdx로 종료");
        //     return;
        // }

        // selectedPoiIdxRef 업데이트를 먼저 수행
        const previousIdx = selectedPoiIdxRef.current;
        selectedPoiIdxRef.current = props.selectedPoiIdx;

        console.log(`[useEffect selectedPoiIdx] selectedPoiIdxRef 업데이트: ${previousIdx} → ${props.selectedPoiIdx}`);

        // 25.09.12 | Kim Young Jun | 마커 직접 클릭인지 확인하여 애니메이션 제어
        // const enableAnimation = !isDirectMarkerClickRef.current;

        if (props.selectedMapIdx && props.selectedMapIdx > 0) {
            overlapMapIDs && selectFeatureOnList(mapObj, selectedPoiIdxRef.current, overlapMapIDs[props.selectedMapIdx! - 1]);
        } else {
            selectFeatureOnList(mapObj, selectedPoiIdxRef.current, baseMapID!);
        }

        // 25.09.12 | 플래그 초기화 (다음 호출을 위해)
        // isDirectMarkerClickRef.current = false;

        // gsshin (2016.01.16) 시간별 이동거리 활성화 상태에 조건을 명확하게 함.
        if ( props.activeIdx === MapToolType.Direction && props.startPoint) {
            // 기존에 그려진 라우트 있을경우 클리어
            const layerGroup = getLayerGroup(mapObj, LayerName.DIRECTION);
            if (!layerGroup) return;

            let routeLayer = getLayer(layerGroup, LayerName.ROUTE);
            let source: VectorSource;

            if (routeLayer) {
                source = routeLayer.getSource() as VectorSource;
                source.clear(); // 기존 라인 clear

                // 기존 툴팁 오버레이 제거
                clearTooltip(mapObj);
            } else {
                source = new VectorSource();
                routeLayer = new VectorLayer({ source });
                routeLayer.set("name", LayerName.ROUTE);
                layerGroup.getLayers().push(routeLayer);
            }

            // 라우트 데이터 호출
            reqRouteData();
        }
    }, [props.selectedPoiIdx]);

    useEffect(() => {
        mapObj && setMapPadding(mapObj, props.mapPadding);
    }, [props.mapPadding]);

    useEffect(() => {
        if (!mapObj || !baseMapID) return;

        handleMapTools(mapObj, props.activeIdx, baseMapID);
        activeIdxRef.current = props.activeIdx;
    }, [props.activeIdx]);

    // 25.01.13 | Kim Young Jun | slopeHook 초기화
    useEffect(() => {
        if (mapObj && props.slopeHook) {
            props.slopeHook.initialize(mapObj, startSlopeAnalysis, stopSlopeAnalysis);
        }
    }, [mapObj, props.slopeHook]);

    useEffect(() => {
        if (!mapObj) return;
        if (props.newPlaces && !props.isAddMode) {
            setNewFeatures(mapObj); // 위치 등록 모드일 경우 미리보기 사용하지 않음
        }
    }, [props.newPlaces]);

    // 주소입력/지도위치 텝 전환 감지
    useEffect(() => {
        mapObj && toggleAddMode(mapObj);
        mapObj && toggleEditMode(mapObj);
    }, [props.isAddMode]);

    // 장소 수정/취소/삭제 시 마커, 지도정보 새로고침
    useEffect(() => {
        if (!mapObj || !baseMapID) return;

        // 25.08.27 | Kim Young Jun | 편집 완료 처리 - 편집 상태 종료
        highlightManager.finishEditing();

        reqMapInfoData(baseMapID);
        console.log("@@@@@ props.editPlaceStatus called reqMapData: ", baseMapID);
        reqMapData(baseMapID, mapObj, "map");
        props.setEditPlaceStatus && props.setEditPlaceStatus(false);
    }, [props.editPlaceStatus]);

    // 편집 모드 상태 변경 감지 및 하이라이트 매니저 동기화
    useEffect(() => {
        const isEditMode = props.isAddMode && props.sideSheetCont === ContentList.EditPlace;
        highlightManager.setEditMode(isEditMode);
    }, [props.isAddMode, props.sideSheetCont]);

    // Analysis 관련 ------------------------------------------------------------------------------------------
    // useEffect(() => {
    //     mapObj && setHeatBlur(mapObj); // 히트맵 블러 조절 함수
    // }, [props.blur]);

    // 25.07.10 | Kim Young Jun | 분석 메트릭 전환 시, 선택된 행정구역에 대한 데이터만 업데이트
    useEffect(() => {
        if (!mapObj || !baseMapID) return;

        // 25.10.02 | Kim Seong Min | 분석할 데이터가 있을 때만 분석 로직 실행
        if (Object.keys(props.allPlaceData).length === 0) return;

        const start = Date.now(); // 시작 시간 기록

        const runAnalysis = async () => {
            switch (props.activeIdx) {
                case MapToolType.Heat:
                    await setHeatBlurByMetric(mapObj);
                    break;
                case MapToolType.Choropleth:
                    await updateAnalysisWithCurrentRegion();
                    break;
            }

            const elapsed = Date.now() - start;
            const delay = Math.max(0, 1500 - elapsed); // 최소 2초 보장

            setTimeout(() => {
                props.setIsLoading && props.setIsLoading(false);
                // props.closeModal && props.closeModal();
            }, delay);
        };

        // 0.5 초 딜레이 줘
        setTimeout(() => runAnalysis(), 500);
    }, [mapObj, props.blur, analysisMetricRef.current, props.allPlaceData, props.analysisTriggerId]); // analysisMetricRef 변경될때 동작

    // 선택된 분석 메트릭 변경 시 props 변경 전달받고 Ref 저장
    useEffect(() => {
        analysisMetricRef.current = props.analysisMetricsToolIdx;
    }, [props.analysisMetricsToolIdx]);

    // 선택된 분석 메트릭(Drpdown) 변경 시 props 변경 전달받고 Ref 저장
    const selectedDropdownFieldRef = useRef<string | null>(null);

    useEffect(() => {
        selectedDropdownFieldRef.current = props.selectedDropdownField ?? null;
    }, [props.selectedDropdownField]);

    useEffect(() => {
        allPlaceDataRef.current = props.allPlaceData;
    }, [props.allPlaceData]);

    // !@#!-OA1-M4-USE1. 오버랩 지도 활성화/비활성화 감지 - URL 파라미터 기반 오버랩 모드 전환
    // 기존 params useEffect - 오버랩 종료만 처리
    useEffect(() => {
        if (!mapObj) return;
        if (overlapMapIDs) {
            console.log("@@@@@@ [params] useEffect 오버랩 모드 감지 - mapInfos useEffect에서 처리");
            // 오버랩 모드는 mapInfos useEffect에서 처리
        } else {
            console.log("@@@@@@ [params] useEffect called stopOverlapMap");
            stopOverlapMap(mapObj);
            // 오버랩 종료 시 처리된 맵 ID 초기화
            processedMapIdsRef.current.clear();
        }
    }, [params]);

    // URL 접근 여부 판단 (초기 로딩 시에만 체크)
    const isInitialURLAccess = useRef<boolean>(false);

    useEffect(() => {
        // 컴포넌트 마운트 시 URL에 오버랩 파라미터가 있으면 URL 접근으로 판단
        if (overlapMapIDs && overlapMapIDs.length > 0) {
            isInitialURLAccess.current = true;
            // console.log("@@@@@@@@@@@@@ [초기화] URL 오버랩 접근으로 감지");
        }
    }, []); // 빈 배열로 마운트 시에만 실행

    // MapScreen에서 오버랩 지도가 하나씩 추가될 때마다 감지하여 처리
    const processedMapIdsRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        if (!mapObj || !props.mapInfos || props.mapInfos.length <= 1) return;

        //console.log("@@@@@@@@@@@@@ [mapInfos] useEffect - mapInfos 변화 감지:", props.mapInfos.length);
        //console.log("@@@@@@@@@@@@@ [mapInfos] URL 접근 여부:", isInitialURLAccess.current);

        // 기본 지도(인덱스 0)를 제외한 오버랩 지도들 처리
        const overlapMaps = props.mapInfos.slice(1);

        // URL 접근이 아닌 경우 (일반 오버랩 추가): 기존 로직 사용, 로딩 스크린 없이 즉시 처리
        if (!isInitialURLAccess.current) {
            //console.log("@@@@@@@@@@@@@ [mapInfos] 일반 오버랩 추가 - 즉시 처리");
            overlapMaps.forEach(async (mapInfo, index) => {
                const mapId = mapInfo.baseMapID || mapInfo.mapId;
                if (!mapId || processedMapIdsRef.current.has(mapId)) {
                    return; // 이미 처리된 지도는 건너뛰기
                }

                //console.log(`@@@@@@@@@@@@@ [mapInfos] 일반 오버랩 지도 처리: ${mapId}`);
                processedMapIdsRef.current.add(mapId);

                try {
                    await startOverlapMap(mapObj, mapId);
                    //console.log(`@@@@@@@@@@@@@ [mapInfos] 일반 오버랩 지도 로드 완료: ${mapId}`);

                    // 투명도 설정
                    setTimeout(() => {
                        switchOverlap(mapObj, mapId);
                    }, 200);
                } catch (error) {
                    console.error(`@@@@@@@@@@@@@ [mapInfos] 일반 오버랩 지도 로드 실패: ${mapId}`, error);
                }
            });
            return;
        }

        // URL 접근인 경우: 순차 로딩
        console.log("@@@@@@@@@@@@@ [mapInfos] URL 접근 - 순차 로딩 시작");

        overlapMaps.forEach(async (mapInfo, index) => {
            const mapId = mapInfo.baseMapID || mapInfo.mapId;
            if (!mapId || processedMapIdsRef.current.has(mapId)) {
                return; // 이미 처리된 지도는 건너뛰기
            }

            // console.log(`@@@@@@@@@@@@@ [mapInfos] URL 접근 오버랩 지도 감지: ${mapId} (${index + 1}/${overlapMaps.length})`);
            processedMapIdsRef.current.add(mapId);

            try {
                await startOverlapMap(mapObj, mapId);
                // console.log(`@@@@@@@@@@@@@ [mapInfos] URL 접근 오버랩 지도 로드 완료: ${mapId}`);

                // 투명도 설정
                setTimeout(() => {
                    switchOverlap(mapObj, mapId);
                }, 100);

                // 마지막 오버랩 지도 로드 완료 시 URL 접근 플래그 리셋
                if (index + 1 === overlapMaps.length) {
                    setTimeout(() => {
                        isInitialURLAccess.current = false;
                        // console.log("@@@@@@@@@@@@@ [mapInfos] URL 접근 완료 - 플래그 리셋");
                    }, 250);
                }
            } catch (error) {
                console.error(`@@@@@@@@@@@@@ [mapInfos] URL 접근 오버랩 지도 로드 실패: ${mapId}`, error);
                // 에러 발생 시에도 URL 접근 플래그 리셋
                if (index + 1 === overlapMaps.length) {
                    isInitialURLAccess.current = false;
                }
            }
        });
    }, [props.mapInfos]);

    useEffect(() => {
        if (!mapObj) return;

        if (props.selectedMapIdx == 0 || props.selectedMapIdx == undefined) {
            switchOverlap(mapObj);
            return;
        }

        overlapMapIDs && switchOverlap(mapObj, overlapMapIDs[props.selectedMapIdx - 1]);
    }, [props.selectedMapIdx]);

    useEffect(() => {
        if (!mapObj || !props.accState) return;

        const fetchAccessibilityData = async () => {
            if (props.accState!.vehicle && props.accState!.ready) {
                props.setIsLoading?.(true);
                props.setLoadingMessage?.("접근성 분석 중입니다.");
                props.postMessage?.({ type: 'WEB_VIEW_LOADING_STATE',  payload: { isLoading: true }}); // 2026.01.19 | dslee | 접근성 분석 로딩 시작 웹뷰 전송
                await reqAccessibilityData(mapObj, props.accState!.vehicle);
                props.setIsLoading?.(false);
                props.postMessage?.({ type: 'WEB_VIEW_LOADING_STATE',  payload: { isLoading: false }}); // 2026.01.19 | dslee | 접근성 분석 로딩 종료 전송
            }
        };

        fetchAccessibilityData();
    }, [props.accState]);

    const prevIsExtentAnalysisRef = useRef<boolean>(props.isExtentAnalysis);

    useEffect(() => {
        // 이전 값이 true였고 현재는 false인 경우 (범위 분석 종료)
        if (prevIsExtentAnalysisRef.current === true && props.isExtentAnalysis === false) {
            // 25.07.25 | Kim Young Jun | 분석 모드 종료 시, 선택된 분석 도구가 다른 분석으로 전환되는 경우
            const isTransitionToOtherAnalysis = [
                MapToolType.Direction,
                MapToolType.Accessibility,
                MapToolType.Overlap,
                MapToolType.Region,
                MapToolType.Choropleth,
                MapToolType.Chart,
                MapToolType.Bubble,
                MapToolType.Heat,
            ].includes(activeIdxRef.current);

            props.setAnalysisXIDs(undefined); // 분석 결과 초기화
            setAnalysisFeatureProperties({});

            // 25.07.07 | Kim Young Jun | 선택된 피처 하이라이트 해제
            selectedPoiIdxRef.current = 0;
            props.setSelectedPoiIdx(0);
            highlightManager.clearAllSelections();

            // 25.07.09 | Kim Young Jun | 범위 분석 종료 시 searchFilter 다시 호출
            if (mapObj) {
                const categoryLabel = getCurrentCategoryLabel();

                setTimeout(() => {
                    searchFilter(
                        mapObj,
                        {
                            category: categoryLabel,
                            search: props.searchOptions?.search,
                        },
                        true, // isAnalysis: true로 설정하여 검색 후 지도 영역을 자동으로 조정하지 않음
                        false, // doFit: false로 설정하여 검색 후 지도 영역을 자동으로 조정하지 않음
                        undefined,
                        isTransitionToOtherAnalysis // 다른 분석으로 전환되는 경우
                    );
                }, 0);
            }
        }

        // 현재 값을 ref에 저장 (항상 마지막에)
        prevIsExtentAnalysisRef.current = props.isExtentAnalysis;
    }, [prevIsExtentAnalysisRef.current, props.isExtentAnalysis]);

    // 25.07.18 | Kim Young Jun | 이전 분석 상태 REF
    const prevAnalysisStateRef = useRef<boolean>(false);

    useEffect(() => {
        prevAnalysisStateRef.current = props.prevAnalysisState ?? false;
    }, [props.prevAnalysisState]);

    // 25.06.19 | Kim Young Jun | 검색 옵션(searchOptions) 변경 시 마커 변경기능 추가
    useEffect(() => {
        props.upadateSelectedCategory();

        if (!mapObj) return;

        const categoryLabel = getCurrentCategoryLabel();

        if (props.isExtentAnalysis && !prevIsExtentAnalysisRef.current) return;

        // 25.07.17 | Kim Young Jun | 선택된 아이템이 있고 닫혔다가 열리는 경우 searchFilter 동작 안하도록
        if (props.selectedPoiIdx !== 0 && props.isClosedSideSheet) {
            props.setIsClosedSideSheet?.(false);
            return;
        }

        if (props.selectedMapIdx === 0 || props.selectedMapIdx === undefined) {
            // 25.09.08 | Kim Young Jun | 경사도 분석은 마커와 함께 표시되므로 항상 isAnalysis=false
            const isSlopeActive = props.slopeHook?.isSlopeActive ?? false;

            // 경사도 분석이 활성화된 경우에는 무조건 isAnalysis를 false로 설정 (마커 표시 유지)
            // 다른 분석의 경우에는 기존 로직을 따름
            const isAnalysisActive = isSlopeActive ? false : prevAnalysisStateRef.current;

            searchFilter(
                mapObj,
                {
                    category: categoryLabel,
                    search: props.searchOptions?.search,
                },
                isAnalysisActive, // 경사도 분석일 때는 false, 다른 분석일 때는 기존 로직 유지
                true // doFit: true로 설정하여 검색 후 지도 영역을 자동으로 조정
            );
            return;
        }

        overlapMapIDs &&
            searchFilter(
                mapObj,
                { category: categoryLabel, search: props.searchOptions?.search },
                false,
                true,
                overlapMapIDs[props.selectedMapIdx - 1]
            );
    }, [props.selectedCategoryArray, props.selectedMapIdx, props.searchOptions, props.forceUpdate, props.slopeHook?.isSlopeActive]);

    useEffect(() => {
        // 받아온 isochronePolygon 그려주기
        if (!mapObj || !props.isochronePolygon) return;
        if (!mapRef.current || !metaData) return;
        const mapProj = getProjection(metaData.mapType);
        addIsochronePolygon(mapObj, props.isochronePolygon, mapProj); // OSM 지도(EPSG:3857)의 좌표계 변환을 위해 srs 옵션 추가 | smyun | 250908
    }, [props.isochronePolygon]);

    useEffect(() => {
        // 버튼으로 분석 레이어 클리어
        if (props.clearByButton !== -1) {
            if (!mapObj || !props.activeIdx) return;
            if (props.activeIdx == MapToolType.Direction) {
                if (props.clearByButton === 0) {
                    // 초기화 요청
                    clearLayerByStage(mapObj, props.activeIdx, props.clearByButton);
                    startDirection(mapObj);
                    props.setAnalysisStage(-1);
                    props.setClearByButton(-1);
                } else {
                    clearLayerByStage(mapObj, props.activeIdx, props.clearByButton);
                    props.setAnalysisStage(props.analysisStage - 1);
                    props.setClearByButton(-1);
                    props.setSelectedPoiIdx(0);
                }
            }
        }
    }, [props.clearByButton]);

    useEffect(() => {
        if (!mapObj) return;
        resetMapTools(mapObj);
        startAccessibility(mapObj);
    }, [props.resetAcc]);

    useEffect(() => {
        if (!mapObj || !baseMapID) return;

        handleMapTools(mapObj, props.activeIdx, baseMapID);
    }, [analysisMetricRef.current, props.extentToolIdx]);

    // 25.07.10 | Kim Young Jun | 단계구분도 최초 활성화 시 Fit
    useEffect(() => {
        if (!mapObj || !metaData) return;

        // isChoroplethAnalysis가 true로 변경되었을 때만 실행
        if (props.isChoroplethAnalysis) {
            mapObj.getView().animate({
                zoom: 1,
                duration: 500,
            });
        }
    }, [props.isChoroplethAnalysis, mapObj]);

    const isOverlayReady = useRef(false);

    useLayoutEffect(() => {
        if (!mapObj || !graphOverlayRef.current || graphOverlay.current || isOverlayReady.current) {
            return;
        }

        graphOverlay.current = new Overlay({
            element: graphOverlayRef.current,
            positioning: "center-center",
            offset: [0, 0],
            stopEvent: false,
        });

        mapObj.addOverlay(graphOverlay.current);
        isOverlayReady.current = true;

        if (!graphRootRef.current) {
            graphRootRef.current = ReactDOM.createRoot(graphOverlayRef.current);
        }
    }, [mapObj, graphOverlayRef.current]);

    useEffect(() => {
        if (graphOverlayRef.current) {
            graphOverlayRef.current.style.display = isOnGraph ? "block" : "none";
        }
    }, [isOnGraph]);

    useEffect(() => {
        if (!props.agreeNoneAddress && revertPositionRef.current) {
            // 편집 취소 - 원본 위치로 복원
            const originalCoords = highlightManager.cancelEditing();
            const coordsToRevert = originalCoords || revertPositionRef.current;

            updateMarkerPosition(coordsToRevert);

            if (translateGeometry.current) {
                translateGeometry.current.setCoordinates(coordsToRevert);
            }
        }
    }, [props.agreeNoneAddress]);

    // 줌 변경 감지 및 지도 줌 조정
    useEffect(() => {
        if (!mapObj || syncing) return;
        adjustZoom(mapObj);
    }, [zoom, mapObj]);

    /**
     * 함수
     */
    const showModal = (message: React.ReactNode) => {
        openModal({
            title: "안내",
            children: message,
            modalButtons: (
                <ModalButtons
                    buttonCount={1}
                    buttons={[
                        {
                            label: "확인",
                            color: "Primary",
                            onClick: () => closeModal(),
                        },
                    ]}
                />
            ),
        });
    };

    const searchFilter = async (
        map: OlMap,
        searchOptions: {
            category?: string;
            search?: string;
        },
        isAnalysis: boolean = false,
        doFit: boolean = true,
        overlappedMapId?: string,
        forceSkipPOIUpdate: boolean = false
    ) => {
        const targetMapId = overlappedMapId ?? baseMapID;

        if (!targetMapId) return;

        if (overlappedMapId) {
            // 오버랩 지도인 경우: 해당 overlappedMapId에 해당하는 오버랩 지도 레이어를 초기화
            const overlapLayerGroup = getLayerGroup(map, LayerName.OVERLAP);

            if (overlapLayerGroup) {
                const overlapLayers = overlapLayerGroup.getLayersArray() as ClusterLayer[];
                const targetOverlapLayer = overlapLayers.find((layer) => layer.get("mapid") === overlappedMapId);

                if (targetOverlapLayer) {
                    // 오버랩 지도 레이어의 스타일을 명시적으로 재설정
                    targetOverlapLayer.setStyle(undefined);
                    targetOverlapLayer.setStyle(clusterStyleFunction);

                    // 기존 인터랙션 제거 (해당 오버랩 지도에 대한)
                    removeInteractions(map, LayerName.OVERLAP);
                }
            }
        } else {
            // 기준 지도인 경우: 기존 클러스터 레이어를 초기화
            const clusterLayer = getLayer(map, LayerName.CLUSTER) as ClusterLayer;

            if (clusterLayer) {
                // 클러스터 레이어의 스타일을 명시적으로 재설정
                clusterLayer.setStyle(undefined);
                clusterLayer.setStyle(clusterStyleFunction);

                // 기존 인터랙션 제거
                removeInteractions(map, LayerName.CLUSTER);
            }
        }

        const isNotUseMarkerAnalysis =
            props.isNotUseMarkerAnalysis ??
            [MapToolType.Region, MapToolType.Choropleth, MapToolType.Chart, MapToolType.Bubble, MapToolType.Heat].includes(activeIdxRef.current);

        const mapInfo = await reqMapInfoData(targetMapId, ["category", "famousMarker"]);

        if (!mapInfo) return;

        // 오버랩 지도인 경우 해당 지도의 메타데이터를 가져와서 사용
        let targetMetaData = metaData;
        if (overlappedMapId && overlappedMapId !== baseMapID) {
            console.log("@@@@@@ searchfilter called reqMetadata: ", overlappedMapId);
            targetMetaData = await reqMetadata(overlappedMapId);
            if (!targetMetaData) return;
        }

        if (!targetMetaData) return;

        let fc = null;
        const isRegistering = registeringOverlapMapIds.current.has(overlappedMapId!);

        if (!isRegistering) {
            fc = await reqFeatureData(targetMapId, targetMetaData);
        }

        if (!fc) return;

        const categoryLabel = searchOptions.category ?? "전체";
        const searchKeyword = searchOptions.search?.trim();

        // console.log("[DEBUG] searchFilter called with options:", categoryLabel, " / ", searchKeyword);

        // 마커 필터링 로직
        const filteredFc = {
            ...fc,
            features: fc.features.filter((feature) => {
                const property = feature.properties || {};

                //const categoryMatch = categoryLabel === "전체" || property.category === categoryLabel;
                const getCategory = (property: any) => property?.category ?? "전체";
                const categoryMatch = categoryLabel === "전체" || getCategory(property) === categoryLabel;

                const searchMatch =
                    !searchKeyword ||
                    property.label?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
                    property.geoField?.toLowerCase().includes(searchKeyword.toLowerCase());

                // 분석 모드가 활성화 되어있을 때
                // 초기화 함수 콜일 경우
                if (!isAnalysis) {
                    if (props.isExtentAnalysis && analysisFeatureProperties.length > 0) {
                        const matchInAnalysis = analysisFeatureProperties.some(
                            (p: { category?: string; label?: string; geoField?: string }) =>
                                p.category === property.category && p.label === property.label && p.geoField === property.geoField
                        );
                        return categoryMatch && searchMatch && matchInAnalysis;
                    }
                }

                return categoryMatch && searchMatch;
            }),
        };

        // 25.07.25 | Kim Young Jun | POI 리스트 업데이트 로직 추가한 내용 조건 수정
        const shouldKeepCurrentPanel = [MapToolType.Direction, MapToolType.Accessibility].includes(props.activeIdx);

        // 25.09.05 | Kim Seong Min | 시간별이동거리 접근성 분석은 POI 리스트 업데이트 방어
        if (!forceSkipPOIUpdate && !shouldKeepCurrentPanel && (isAnalysis || !props.isExtentAnalysis)) {
            updatePOIListFromFeatures(filteredFc.features, props.setAnalysisXIDs);
        }

        // 25.07.21 | Kim Young Jun | 분석 도구 활성화 시 fit 방지 로직 개선
        const shouldPreventFit = isAnalysis || props.isAnalysisActive || props.isExtentAnalysis;

        // 25.09.08 | Kim Young Jun | 필터링된 결과 저장 (카테고리 검증용)
        lastFilteredResultsRef.current = filteredFc.features;

        if (overlappedMapId) {
            setOverlapFeatures(map, filteredFc, targetMapId, mapInfo, targetMetaData.metadata);
        } else {
            // fit 방지가 필요한 경우 doFit을 false로 전달
            setFeatures(map, filteredFc, targetMapId, shouldPreventFit, !shouldPreventFit);
        }

        // 분석 모드가 활성화되어 있으면 마커를 렌더링하지 않음
        if (isNotUseMarkerAnalysis) {
            const clusterLayer = getLayer(map, LayerName.CLUSTER);
            clusterLayer?.setVisible(false);
            // const featureLayer = getLayer(map, LayerName.FEATURE);
            // featureLayer?.setVisible(false);
        }
    };

    const saveCoords = (coords: number[]) => {
        props.setNewPlace([
            {
                1: {
                    coords: coords,
                    label: "",
                    category: "",
                },
            },
        ]);
    };

    const setAccState = useCallback(
        (features: OlFeature[]) => {
            const isReady = features.length >= accessibilityCondition.MIN_POINTS && features.length <= accessibilityCondition.MAX_POINTS;
            props.setAccState(undefined, isReady);
        },
        [props.setAccState]
    );

    /**
     * 피처 로드 완료 여부 확인 함수
     */
    const isCompleted = () => {
        const status = getfeatureStatus.current;
        if (!status) return false;

        // true일 경우 피처 데이터 받아오기 성공,
        // false일 경우 대기중
        // 데이터 수신 실패한 경우 목록에서 제거
        const notLoaded = Object.keys(status).filter((key) => status[Number(key)] === false);
        return notLoaded.length === 0;
    };

    // 계산/분석 도구 ------------------------------------------------------------------
    const handleMapTools = (map: OlMap, type: MapToolType, mapid: string) => {
        // 25.01.13 | Kim Young Jun | 경사도의 경우 다른 도구를 정리하되 경사도는 유지
        if (type === MapToolType.Slope) {
            // 경사도가 아닌 다른 도구들만 정리
            resetMapToolsExceptSlope(map);
        } else {
            resetMapTools(map); // 경사도 포함 모든 도구 초기화
        }
        setIsOnGraph(false);

        props.setIsExtentAnalysis(false); // 범위분석 비활성화

        const searchOptions = { category: getCurrentCategoryLabel(), search: props.searchOptions?.search };

        switch (type) {
            case MapToolType.Region:
                startRegionAnalysis(map, mapid);
                break;
            case MapToolType.Choropleth:
                startChoroplethMap(map, mapid);
                break;
            case MapToolType.Chart:
                startChartMap(map, mapid);
                break;
            case MapToolType.Heat:
                startHeatMap(map);
                break;
            case MapToolType.Bubble:
                startBubbleMap(map);
                break;
            case MapToolType.Direction:
                startDirection(map);
                break;
            case MapToolType.ExtentAnalysis:
                // console.log("[DEBUG] 범위분석 시작");
                props.extentToolIdx == 25
                    ? startExtentAnalysisPolygon(map, mapid, searchOptions)
                    : startExtentAnalysisCircle(map, mapid, searchOptions);
                break;
            case MapToolType.Accessibility:
                startAccessibility(map);
                break;
            case MapToolType.Slope:
                if (props.slopeHook) {
                    props.slopeHook.startSlopeAnalysis(map);
                } else {
                    startSlopeAnalysis(map);
                }
                break;

            // 계산도구
            case MapToolType.Distance:
                startMeasure(map, CalToolType.LineString, "distance", "measure");
                break;
            case MapToolType.Area:
                startMeasure(map, CalToolType.PolygonRing, "extent", "measure");
                break;
            case MapToolType.Radius:
                startMeasure(map, CalToolType.Circle, "radius", "measure");
                break;
        }
    };

    /**
     * 분석 도구 초기화 함수
     * @param map
     */
    const resetMapTools = (map: OlMap) => {
        map.getLayers()
            .getArray()
            .forEach((layer: BaseLayer) => {
                const name = layer.get("name") as LayerName;
                const visibleLayers = [LayerName.BASE, LayerName.CLUSTER];

                // 기본 레이어 보이기
                layer.setVisible(visibleLayers.includes(name));
            });

        stopChartMap(map); // 단계구분도 종료
        stopRegionMap(map); // 행정구역 종료
        stopChoroplethMap(map); // 단계구분도 종료
        stopDirection(map); // 시간별이동거리 종료
        stopMeasure(map); // 측정 종료
        stopAccessibility(map); // 접근성 분석 종료

        // 25.01.13 | Kim Young Jun | 경사도 분석 종료 추가
        if (props.slopeHook) {
            props.slopeHook.stopSlopeAnalysis(map);
        }
    };

    /**
     * 경사도를 제외한 분석 도구 초기화 함수
     * @param map
     */
    const resetMapToolsExceptSlope = (map: OlMap) => {
        map.getLayers()
            .getArray()
            .forEach((layer: BaseLayer) => {
                const name = layer.get("name") as LayerName | string;
                const visibleLayers = [LayerName.BASE, LayerName.CLUSTER, LayerName.SLOPE, "slope_analysis"];

                // 기본 레이어 + 경사도 레이어 보이기
                layer.setVisible(visibleLayers.includes(name as LayerName) || visibleLayers.includes(name as string));
            });

        stopChartMap(map); // 단계구분도 종료
        stopRegionMap(map); // 행정구역 종료
        stopChoroplethMap(map); // 단계구분도 종료
        stopDirection(map); // 시간별이동거리 종료
        stopMeasure(map); // 측정 종료
        stopAccessibility(map); // 접근성 분석 종료

        // 경사도는 종료하지 않음
    };

    /**
     * 단계구분도/행정구역별분석/차트 분석용 행정경계 폴리곤 데이터 로드 함수
     * @param map {@link OlMap} OL Map 객체
     */
    const setRegionPolygons = (map: OlMap) => {
        Object.values(RegionType).forEach((type) => {
            reqRegionPolygons(type, map);
        });
    };

    /**
     * 행정경계 - 분석 기능 시작 함수
     * @author kmy
     * @since 2024.04.30
     */
    const startRegionAnalysis = (map: OlMap, mapid: string) => {
        const name = LayerName.REGION;
        const layerGroup = getLayerGroup(map, name);
        const upperRegion = "0"; // default: 0 => 전국
        if (!layerGroup) return;
        if (!layerGroup.get("hasData")) {
            reqAnalysisData(map, mapid, upperRegion, name, true, layerGroup, analysisMetricRef.current!);
        } else {
            props.setLegend?.(legendData["0"]);
        }

        const filterFunction = (f: OlFeature) => f.get("focus");
        const layers = layerGroup.getLayersArray();
        const [select, hover] = [
            new Select({
                layers: layers,
                filter: filterFunction,
                style: regionStyleFunction,
            }),
            new Hover({
                cursor: "pointer",
                featureFilter: filterFunction,
                layers: layers,
                handleEvent: () => true,
            }),
        ];

        select.setProperties({ name: name });
        hover.setProperties({ name: name });

        select.getFeatures().on("add", (e: CollectionEvent<OlFeature<Geometry>>) => onSelectRegion(e, map, select));
        select.getFeatures().on("remove", (e: CollectionEvent<OlFeature<Geometry>>) => e.element.unset("selected"));

        hover.on("enter", onHoverEnter);
        hover.on("leave", () => onHoverLeave(layerGroup, map));

        map.addInteraction(select);
        map.addInteraction(hover);

        layerGroup.setProperties({ hasData: true });

        map.on("moveend", onMoveRegionHandler(name));

        map.getView().animate({
            zoom: 1,
            duration: 500,
        });

        switchLayer(map, layerGroup);
    };

    /**
     * 단계구분도 - 행정구역 분석 기능 종료 함수
     * @param map {@link OlMap} OL Map 객체
     */
    const stopRegionMap = (map: OlMap) => {
        const name = LayerName.REGION;
        const layerGroup = getLayerGroup(map, name);

        removeInteractions(map, name);
        map.un("moveend", onMoveRegionHandler(name));
        layerGroup?.setVisible(false);
    };

    /**
     * 단계구분도 - 분석 기능 시작 함수
     * @param map {@link OlMap} OL Map 객체
     */
    const startChoroplethMap = (map: OlMap, mapid: string) => {
        const name = LayerName.CHOROPLETH;
        const layerGroup = getLayerGroup(map, name);
        const upperRegion = "0"; // default: 0 => 전국

        if (!layerGroup) return;

        const currentMetricIdx = analysisMetricRef;
        const lastMetricIdx = layerGroup.get("hasDataMetricIdx");

        if (lastMetricIdx !== currentMetricIdx) {
            reqAnalysisData(map, mapid, upperRegion, name, true, layerGroup, analysisMetricRef.current!);
            layerGroup.set("hasDataMetricIdx", currentMetricIdx); // 현재 메트릭 저장
        } else {
            props.setLegend?.(legendData["0"]);
        }

        const filterFunction = (f: OlFeature) => f.get("focus");
        const layers = layerGroup.getLayersArray();
        const [select, hover] = [
            new Select({
                layers: layers,
                filter: filterFunction,
                style: choroplethStyleFunction,
            }),
            new Hover({
                cursor: "pointer",
                featureFilter: filterFunction,
                layers: layers,
                handleEvent: () => true,
            }),
        ];

        labelOverlay.current = createLabelOverlay(name, `${styles.choroplethMap_label_overlay}`, [-10, 0]);

        labelOverlay.current.setProperties({ name: name });
        select.setProperties({ name: name });
        hover.setProperties({ name: name });

        // 클릭 시
        // select 객체는 기본이 1개 선택(multi false) / 줌아웃 될 때 선택 clear 됨 /  zoomInto 하면서 상위 행정구역 select 해제
        select.getFeatures().on("add", (e: CollectionEvent<OlFeature<Geometry>>) => onSelectRegion(e, map, select));
        select.getFeatures().on("remove", (e: CollectionEvent<OlFeature<Geometry>>) => {
            e.element.unset("selected");
            // smyun | 상위 행정구역 hover시 표출되는 Label 제거
            if (labelOverlay) map.removeOverlay(labelOverlay.current!);
        });

        // 마우스 오버 시
        hover.on("enter", onHoverEnter);
        hover.on("leave", () => onHoverLeave(layerGroup, map));

        map.addInteraction(select);
        map.addInteraction(hover);
        map.addOverlay(labelOverlay.current);

        layerGroup.setProperties({ hasData: true });

        map.on("moveend", onMoveRegionHandler(name));

        switchLayer(map, layerGroup);
    };

    /**
     * 단계구분도 - 분석 기능 종료 함수
     * @param map {@link OlMap} OL Map 객체
     */
    const stopChoroplethMap = (map: OlMap) => {
        const name = LayerName.CHOROPLETH;
        const layerGroup = getLayerGroup(map, name);

        removeOverlays(map, name); // Label overlay div 해제
        removeInteractions(map, name);
        map.un("moveend", onMoveRegionHandler(name));
        layerGroup?.setVisible(false);
    };

    /**
     * 차트분석 - 차트 분석 기능 시작 함수
     * @param map {@link OlMap} OL Map 객체
     */
    const startChartMap = async (map: OlMap, mapid: string) => {
        const name = LayerName.CHART;
        const layerGroup = getLayerGroup(map, name);
        const upperRegion = "0"; // 전국

        if (!layerGroup) return;

        // 1) 데이터 주입 ------------------------------------------------
        if (!layerGroup.get("hasData")) {
            await reqAnalysisData(map, mapid, upperRegion, name, true, layerGroup, analysisMetricRef.current!);
        }

        // 2) 레이어 그룹 스타일 세팅 ------------------------------------------------
        const targetType = getRegionType(upperRegion);
        const layers = layerGroup.getLayersArray();

        layers.forEach((layer) => {
            const isTarget = layer.get("name") === targetType;

            if (isTarget) {
                layer.setVisible(true);
                (layer as VectorLayer<VectorSource>).setStyle(chartStyleFunction);
            } else {
                layer.setVisible(false);
            }
        });

        // 3) 레이어 인터렉션 연결 ------------------------------------------------
        const filterFunction = (f: OlFeature) => !!f.get("focus");

        // 2026.01.16 | dslee | 모바일인 경우 다르게 처리, OneClick : 지역 선택(차트 렌더링), DoubleClick(확대)
        if (props.isMobileView) {
            // 1. 더블클릭 -> 지역 확대/선택
            const selectZoom = new Select({
                layers: layers,
                filter: filterFunction,
                style: chartStyleFunction,
                condition: doubleClick, // 모바일은 더블 탭 시 확대
            });

            // 2. 싱글클릭 -> 차트 표출 및 색상 변경
            const selectChart = new Select({
                layers: layers,
                filter: filterFunction,
                style: chartStyleFunction,
                condition: singleClick, // 모바일은 싱글 탭 시 차트
            });

            selectZoom.setProperties({ name: name });
            selectChart.setProperties({ name: name });

            selectZoom.getFeatures().on("add", (e: CollectionEvent<OlFeature<Geometry>>) => {onSelectRegion(e, map, selectZoom);});

            selectChart.getFeatures().on("add", (e: CollectionEvent<OlFeature<Geometry>>) => {
                const feature = e.element;
                const layer = selectChart.getLayer(feature);
                // 수동으로 hover 속성 부여 (연한 파란색으로 변경됨)
                feature.set("hover", true); 
                
                onChartHoverEnter({
                    feature: feature,
                    map: map,
                    layer: layer
                } as any);
            });

            // 취소 시 색상 원복 및 차트 닫기
            // 다른 지역을 누르거나 지도의 빈 곳을 누르면 'remove' 이벤트 발생
            selectChart.getFeatures().on("remove", (e: CollectionEvent<OlFeature<Geometry>>) => {
                const feature = e.element;
                feature.unset("hover"); // 수동으로 넣었던 hover 속성 제거 (색상 원복)
                feature.unset("selected"); // 선택 속성 제거
                setIsOnGraph(false); // 차트 숨기기
                
                if (mapObj) {
                    updateOverlayChartLabel(map, undefined);
                }
            });

            map.addInteraction(selectZoom);
            map.addInteraction(selectChart);
        } else {
            const select = new Select({
                layers: layers,
                filter: filterFunction,
                style: chartStyleFunction,
                condition: singleClick, // 웹은 클릭 시 즉시 확대
            });

            const hover = new Hover({
                cursor: "pointer",
                featureFilter: filterFunction,
                layers: layers,
                handleEvent: () => true,
            });

            select.setProperties({ name: name });
            hover.setProperties({ name: name });

            select.getFeatures().on("add", (e: CollectionEvent<OlFeature<Geometry>>) => onSelectRegion(e, map, select));
            select.getFeatures().on("remove", (e: CollectionEvent<OlFeature<Geometry>>) => {
                e.element.unset("selected");
                setIsOnGraph(false);
            });

            hover.on("enter", onChartHoverEnter);
            hover.on("leave", () => onHoverLeave(layerGroup, map));

            map.addInteraction(select);
            map.addInteraction(hover);
        }

        // 라벨 오버레이 추가 (공통)
        labelOverlay.current = createLabelOverlay(name, `${styles.chartMap_label_overlay}`, [0, -130]);
        labelOverlay.current.setProperties({ name: name });
        map.addOverlay(labelOverlay.current);

        layerGroup.setProperties({ hasData: true });
        map.on("moveend", onMoveRegionHandler(name));

        map.getView().animate({
            zoom: 1,
            duration: 500,
        });

        switchLayer(map, layerGroup);
    };

    /**
     * 단계구분도 - 차트 분석 기능 종료 함수
     * @param map {@link OlMap} OL Map 객체
     */
    const stopChartMap = (map: OlMap) => {
        const name = LayerName.CHART;
        const layerGroup = getLayerGroup(map, name);

        removeOverlays(map, name); // Label overlay div 해제
        removeInteractions(map, name);
        map.un("moveend", onMoveRegionHandler(name));
        layerGroup?.setVisible(false);
    };

    /**
     * 단계구분도 - 상위 행정구역 레이어에 라벨 오버레이 생성
     * 라벨에 border-raius된 div로 표현해주기 위해서 overlay로 div 추가 (시미)
     * @param name {@link string} 레이어 이름
     * @param className {@link string} CSS 클래스 이름
     * @returns {@link Overlay} 오버레이 객체
     */
    const createLabelOverlay = (name: string, className: string, offset: number[]) => {
        const overlayLabelElement = document.createElement("div");
        overlayLabelElement.className = className;

        const labelOverlay = new Overlay({
            element: overlayLabelElement,
            positioning: "center-center",
            stopEvent: false,
            offset: offset,
        });

        labelOverlay.setProperties({ name });

        return labelOverlay;
    };

    /**
     * 단계구분도 - 상위 행정구역 선택 후 해당 구역으로 줌인(확대) 시 실행 함수
     *
     * @param select {@link Select} 인터랙션 객체
     * @param upperRegion {@link OlFeature} 선택된 상위 행정구역 feature 객체
     */
    const zoomIntoSelectedRegion = (select: Select, upperRegion: OlFeature) => {
        const map = select.getMap();
        const upperLayer = select.getLayer(upperRegion);

        if (!map) return;

        const upperRegionType = upperLayer.get("name") as string;
        const upperRegionCode = upperRegion.get(`${upperRegionType}_CD`);
        const regionType = getRegionType(upperRegionCode, "lower");

        props.onRegionChange?.({
            regionCode: upperRegionCode,
            zoomDirection: 'lower'
        });

        if (!regionType || !baseMapID || !upperRegion.get("count")) return;

        const layerGN = upperLayer.get("group"); // 분석도구 group 이름 찾기
        const layerG = getLayerGroup(map, layerGN);
        const layer = layerG && getLayer(layerG, regionType);
        // const layer = getLayer(map, regionType);
        // const layer = layerGroup?.getLayersArray().find((l) => l.get("name") === regionType) as VectorLayer<VectorSource>;
        const source = layer?.getSource() as VectorSource;

        if (!layer || !source) return;

        select.getFeatures().clear(); // 1. 선택된 상위 행정구역 삭제
        upperLayer.setVisible(false); // 2. 상위 행정구역 레이어 숨김

        // upperRegion.get("hasDataMetricIdx") !==
        if (!upperRegion.get("hasData") || analysisMetricRef.current !== 21) {
            // 3. 하위 행정구역 데이터 호출 (hasData가 없거나, metric index가 다를 경우 재요청)
            console.log("[DBG] zoomIntoSelectedRegion → Requesting Data: hasData = false or metric index mismatch");
            const layerGroupName = layerG!.get("name") as LayerName;
            // console.log('[DBG] zoomIntoSelectedRegion → Requesting Data: hasData =', upperRegion.get("hasData"), ', hasDataMetricIdx =', upperRegion.get("hasDataMetricIdx"), ', currentMetricIdx =', analysisMetricRef.current);
            reqAnalysisData(map, baseMapID, upperRegionCode, layerGroupName, false, upperRegion, analysisMetricRef.current!);
        } else {
            // 3-1. 이미 동일 metric 데이터가 있을 경우
            // console.log('[DBG] zoomIntoSelectedRegion → Using Cached Data (metric matches)');

            source.getFeatures().forEach((featureLike) => {
                const feature = featureLike as OlFeature;
                const regionCode = feature.get(`${regionType}_CD`) as string;

                if (regionType === RegionType.CTPRVN || regionCode.startsWith(upperRegionCode)) {
                    feature.setProperties({ focus: true });
                } else {
                    feature.unset("focus");
                }
            });

            props.setLegend?.(legendData[upperRegionCode]); // 4. 범례 교체
        }

        layer.setVisible(true); // 5. 하위 행정구역 레이어 펼침
    };

    /**
     * 단계구분도 - 줌아웃 시 실행 함수
     *
     * @param select {@link Select} select 인터랙션 객체
     * @param lowerLayer {@link Layer} 하위 행정구역 레이어
     */
    const zoomOutOfSelectedRegion = (select: Select, lowerLayer: Layer) => {
        const map = select.getMap();
        const lowerSource = lowerLayer.getSource() as VectorSource;
        const lowerRegionType = lowerLayer.get("name");
        // @@ Need to Check @@
        const lowerRegionCode = lowerSource
            .getFeatures()
            .find((f) => f.get("focus"))
            ?.get(`${lowerRegionType}_CD`) as string;

        const regionType = getRegionType(lowerRegionCode, "upper");

        if (!map || !lowerRegionType || !lowerRegionCode || !regionType) return;

        const lowerLayerGN = lowerLayer.get("group"); // 분석도구 group 이름 찾기
        const layerGroup = getLayerGroup(map, lowerLayerGN);

        select.getFeatures().clear(); // 1. 선택된 행정구역이 있는 경우 제거
        lowerLayer.setVisible(false); // 2. 하위 행정구역 레이어 숨김

        // 3. 상위 행정구역 레이어 찾기
        const layer = layerGroup?.getLayersArray().find((l) => l.get("name") === regionType) as VectorLayer<VectorSource>;
        const source = layer?.getSource() as VectorSource;

        let region: OlFeature | undefined;

        if (regionType === RegionType.COUNTRY) {
            region = source.getFeatures().find((f) => f.get("COUNTRY_CD") === "0");
        } else {
            region = source.getFeatures().find((f) => lowerRegionCode.startsWith(f.get(`${regionType}_CD`)));
        }
        const regionCode = region?.get(`${regionType}_CD`);

        // 25.07.10 | Kim Young Jun | 상위 행정구역 코드와 타입 설정
        props.setCurrentRegionCode(regionCode || "0");
        
        // 2026.01.15 | dslee | 지도 Zoom 변경 데이터 전송용 
        props.onRegionChange?.({
            regionCode: regionCode || '0',
            zoomDirection: 'upper'
        });

        const isChartLayer = (layerGroup!.get("name") as string) === LayerName.CHART;

        if (!layer || !source || !region || !regionCode) return;

        let upperRegionCode = regionCode; // 상위 행정구역 코드

        if (!isChartLayer) {
            upperRegionCode = getUpperRegionCode(regionCode);
        }

        if (!upperRegionCode) return;

        // 4. 상위 행정구역 피처 노출
        if (isChartLayer) {
            if (regionType === RegionType.COUNTRY) {
                // console.log('[DBG] zoomOutOfSelectedRegion COUNTRY');
                source.getFeatures().forEach((f) => f.setProperties({ focus: true }));
            } else {
                source.getFeatures().forEach((f) => {
                    const _regionCode = f.get(`${regionType}_CD`) as string;
                    if (_regionCode?.startsWith(upperRegionCode)) f.setProperties({ focus: true });
                });
            }
        } else {
            if (regionType === RegionType.CTPRVN) {
                source.getFeatures().forEach((f) => f.setProperties({ focus: true }));
            } else {
                source.getFeatures().forEach((f) => {
                    const _regionCode = f.get(`${regionType}_CD`) as string;
                    if (_regionCode?.startsWith(upperRegionCode)) f.setProperties({ focus: true });
                });
            }
        }

        props.setLegend?.(legendData[upperRegionCode]); // 5. 범례 교체
        layer.setVisible(true); // 6. 상위 행정구역 레이어 펼침
    };

    const startHeatMap = (map: OlMap) => {
        const layer = getLayer(map, LayerName.HEAT) as HeatmapLayer;
        const clusterLayer = getLayer(map, LayerName.CLUSTER) as ClusterLayer;
        const clusterSource = clusterLayer.get("source") as ClusterSource;
        const vectorSource = clusterSource.getSource() as VectorSource;

        if (!layer || !clusterLayer || !clusterSource || !vectorSource) return;

        layer.setSource(vectorSource);

        switchLayer(map, layer);
    };

    const startBubbleMap = (map: OlMap) => {
        const layer = getLayer(map, LayerName.BUBBLE) as ClusterLayer;
        const clusterLayer = getLayer(map, LayerName.CLUSTER) as ClusterLayer;
        const clusterSource = clusterLayer.get("source") as ClusterSource;
        const vectorSource = clusterSource.getSource() as VectorSource;

        if (!layer || !clusterLayer || !vectorSource) return;

        const newClusterSource = new ClusterSource({
            distance: 100,
            source: vectorSource,
        });

        layer.setSource(newClusterSource);
        layer.setStyle(getClusterStyle as StyleLike);

        switchLayer(map, layer);
    };

    /**
     * 경사도 실행 함수
     * @param map {@link OlMap} 지도 객체
     */
    const startSlopeAnalysis = (map: OlMap) => {
        const slopeLayer = createSlopeLayer(); // 경사도 레이어 생성
        slopeLayer.setOpacity(0.5);
        slopeLayer.setMinZoom(props.minSlopeZoomLv);
        const wms = slopeLayer.getSource();
        if (wms) {
            wms.on("imageloadstart", () => {
                props.setIsLoading?.(true);
                props.setLoadingMessage?.("경사도 데이터를 불러오는 중입니다.");
            });

            wms.on("imageloadend", () => {
                props.setIsLoading?.(false);
                props.setLoadingMessage?.("");
            });

            wms.on("imageloaderror", () => {
                props.setToast?.({
                    type: ToastType.WARNING,
                    message: "경사도 데이터 요청 중 문제가 발생했습니다.",
                    iconType: "addTask",
                });
                props.setIsLoading?.(false);
                props.setLoadingMessage?.("");
            });
        } else {
            console.log("wms getmap source 를 받아올 수 없습니다.");
        }
        map.addLayer(slopeLayer);
    };

    /**
     * 경사도 종료 함수
     * @param map {@link OlMap} 지도 객체
     */
    const stopSlopeAnalysis = (map: OlMap) => {
        const layers = map.getLayers().getArray();
        const slopeLayersToRemove: BaseLayer[] = [];

        layers.forEach((layer: BaseLayer) => {
            const name = layer.get("name");
            // LayerName.SLOPE나 slope_analysis name을 가진 레이어 제거
            if (name === LayerName.SLOPE || name === "slope_analysis") {
                slopeLayersToRemove.push(layer);
            }
            // createSlopeLayer로 생성된 레이어는 TileLayer이므로 타입 체크 후 URL 확인
            else if (layer.getClassName() === "ol-layer" && "getSource" in layer) {
                const source = (layer as any).getSource();
                if (source && source.getUrls) {
                    const urls = source.getUrls();
                    if (urls && Array.isArray(urls) && urls.some((url: string) => url.includes("rugged"))) {
                        slopeLayersToRemove.push(layer);
                    }
                }
            }
        });

        slopeLayersToRemove.forEach((layer) => {
            map.removeLayer(layer);
        });
    };

    /**
     * 분석도구 > 범위분석 - 범위 폴리곤 그리기 함수
     * @author kmy
     * @since 2025-04-29
     * @param map {@link OlMap} Ol 지도 객체
     */

    // 다각형 폴리곤
    const startExtentAnalysisPolygon = async (map: OlMap, mapid: string, searchOptions: { category?: string; search?: string }) => {
        props.setIsExtentAnalysis(true); // 범위분석 활성화

        const draw = startMeasure(map, CalToolType.PolygonRing, "extent", "analysis");

        // 범위 분석 초기화
        const categoryLabel = getCurrentCategoryLabel();
        searchFilter(
            mapObj!,
            {
                category: categoryLabel,
                search: props.searchOptions?.search,
            },
            true, // 범위 분석 초기화 시 true
            false // 범위 분석 중 화면 Fit 하지 않도록 false
        );

        draw?.on("drawend", async (e: DrawEvent) => {
            const drawPolygon = highLightExtent(e, props.extentToolIdx);
            map.removeInteraction(draw); // 폴리곤 그리기 종료

            // 폴리곤 꼭짓점 좌표 추출
            const coords: Coordinate[][] = getPolygonVerticesFromGeometry(drawPolygon!);
            const polygon = new OlPolygon(coords);
            const mapProj = map.getView().getProjection().getCode();
            const filter = createPolygonIntersectsFilterData(polygon, mapProj);

            const response = await DataProvider.GetFeatureList({ mapid }, { ogcfilter: { filter: filter } });

            if (!response.flag || !response.result) return;

            const result = response.result;

            if (!metaData) return;

            const metaFields = metaData.metadata;
            const fieldNameMap = metaFields.fieldName;

            // 251125 | smyun | 안전화 처리 추가(category가 null/undefined 있음)
            const categoryKey = metaFields.category ? fieldNameMap[metaFields.category] : null;
            const placeNameKey = fieldNameMap[metaFields.placeName];
            const geoFieldKey = fieldNameMap[metaFields.geoField];

            const categoryLabel = searchOptions.category ?? "전체";
            const searchKeyword = searchOptions.search?.trim().toLowerCase() ?? "";

            // 전체 feature 전처리
            const processedFeatures = result.features.map((feature: any) => {
                const props = feature.properties;
                props["category"] = categoryKey ? props[categoryKey] ?? null : null;
                props["label"] = props[placeNameKey];
                props["geoField"] = props[geoFieldKey];

                return feature;
            });

            // 분석 기준 전체 feature 저장 (검색 조건 없이)
            setAnalysisFeatureProperties(processedFeatures.map((feature: any) => feature.properties ?? {}));

            // 검색/카테고리 필터링된 feature
            const filteredFeatures = processedFeatures.filter((feature: any) => {
                const props = feature.properties;

                const categoryMatch = categoryLabel === "전체" || props["category"] === categoryLabel;
                const searchMatch =
                    searchKeyword === "" ||
                    props["label"]?.toLowerCase().includes(searchKeyword) ||
                    props["geoField"]?.toLowerCase().includes(searchKeyword);

                return categoryMatch && searchMatch;
            });

            // 25.07.04 | Kim Young Jun | 클러스터 레이어 스타일 초기화
            // const clusterLayer = getLayer(map, LayerName.CLUSTER) as ClusterLayer;
            // if (clusterLayer) {
            //     // 클러스터 레이어의 스타일을 명시적으로 재설정
            //     clusterLayer.setStyle(null);
            //     clusterLayer.setStyle(clusterStyleFunction);

            //     // 기존 인터랙션 제거
            //     removeInteractions(map, LayerName.CLUSTER);
            // }

            // 25.07.18 | Kim Young Jun | 범위 분석 시 fit 안되도록 true 매개변수 전달
            const isExtendAnalysis = true;

            setFeatures(map, { ...result, features: filteredFeatures }, mapid, isExtendAnalysis);

            // 25.07.09 | Kim Young Jun | XID 추출 및 POI 리스트 업데이트 로직 모듈화
            updatePOIListFromFeatures(filteredFeatures, props.setAnalysisXIDs);
        });
    };

    const startExtentAnalysisCircle = async (map: OlMap, mapid: string, searchOptions: { category?: string; search?: string }) => {
        props.setIsExtentAnalysis(true); // 범위분석 활성화

        // 25.07.01 | Kim Young Jun | 그리기 스타일 변경
        const draw = startMeasure(map, CalToolType.Circle, "extent", "analysis");

        // 범위 분석 초기화
        const categoryLabel = getCurrentCategoryLabel();
        searchFilter(
            mapObj!,
            {
                category: categoryLabel,
                search: props.searchOptions?.search,
            },
            true // 범위 분석 초기화 시 true
        );

        draw?.on("drawend", async (e: DrawEvent) => {
            highLightExtent(e, props.extentToolIdx);
            map.removeInteraction(draw); // 폴리곤 그리기 종료

            // 원형 Polygon 생성
            const feature = e.feature as OlFeature;
            const line = feature.getGeometry() as LineString;
            const coords = line.getCoordinates();

            if (coords.length !== 2) return;

            const center = line.getFirstCoordinate(); // 원 중심
            const center_ = fromLonLat(center, "EPSG:5179");
            const radius = line.getLength();

            const circle = new Circle(center_, radius);

            // 원을 다각형으로 변환 (기본적으로 원을 근사하는 다각형 생성)
            const coordinates = circle.getCoordinates(); // 원의 좌표 얻기
            const numPoints = 64; // 원을 근사할 때 사용할 점의 수 (각도를 나누는 개수)

            const polygonCoords = [];
            for (let i = 0; i < numPoints; i++) {
                const angle = (i / numPoints) * 2 * Math.PI; // 각도 계산
                const x = center[0] + radius * Math.cos(angle); // x 좌표
                const y = center[1] + radius * Math.sin(angle); // y 좌표
                polygonCoords.push([x, y]);
            }

            // 폴리곤 생성
            const polygon = new OlPolygon([polygonCoords]);

            const mapProj = map.getView().getProjection().getCode();
            const filter = createPolygonIntersectsFilterData(polygon, mapProj);

            const response = await DataProvider.GetFeatureList({ mapid }, { ogcfilter: { filter: filter } });

            if (!response.flag || !response.result) return;

            const result = response.result;

            if (!metaData) return;

            const metaFields = metaData.metadata;
            const fieldNameMap = metaFields.fieldName;

            // 251125 | smyun | 안전화 처리 추가(category가 null/undefined 있음)
            const categoryKey = metaFields.category ? fieldNameMap[metaFields.category] : null;
            const placeNameKey = fieldNameMap[metaFields.placeName];
            const geoFieldKey = fieldNameMap[metaFields.geoField];

            const categoryLabel = searchOptions.category ?? "전체";
            const searchKeyword = searchOptions.search?.trim().toLowerCase() ?? "";

            // 전체 feature 전처리
            const processedFeatures = result.features.map((feature: any) => {
                const props = feature.properties;
                props["category"] = categoryKey ? props[categoryKey] ?? null : null;
                props["label"] = props[placeNameKey];
                props["geoField"] = props[geoFieldKey];
                return feature;
            });

            // 분석 기준 전체 feature 저장 (검색 조건 없이)
            setAnalysisFeatureProperties(processedFeatures.map((feature: any) => feature.properties ?? {}));

            // 검색/카테고리 필터링된 feature
            const filteredFeatures = processedFeatures.filter((feature: any) => {
                const props = feature.properties;

                const categoryMatch = categoryLabel === "전체" || props["category"] === categoryLabel;
                const searchMatch =
                    searchKeyword === "" ||
                    props["label"]?.toLowerCase().includes(searchKeyword) ||
                    props["geoField"]?.toLowerCase().includes(searchKeyword);

                return categoryMatch && searchMatch;
            });

            // 25.07.04 | Kim Young Jun | 클러스터 레이어 스타일 초기화
            const clusterLayer = getLayer(map, LayerName.CLUSTER) as ClusterLayer;
            if (clusterLayer) {
                // 클러스터 레이어의 스타일을 명시적으로 재설정
                clusterLayer.setStyle(null);
                clusterLayer.setStyle(clusterStyleFunction);

                // 기존 인터랙션 제거
                removeInteractions(map, LayerName.CLUSTER);
            }

            // 25.07.18 | Kim Young Jun | 범위 분석 시 fit 안되도록 true 매개변수 전달
            const isExtendAnalysis = true;

            setFeatures(map, { ...result, features: filteredFeatures }, mapid, isExtendAnalysis);

            // 25.07.09 | Kim Young Jun | XID 추출 및 POI 리스트 업데이트 로직 모듈화
            updatePOIListFromFeatures(filteredFeatures, props.setAnalysisXIDs);
        });
    };

    /**
     * 25.07.09 | Kim Young Jun | 피처 목록에서 XID를 추출하여 POI 리스트를 업데이트하는 함수
     * @param features 필터링된 피처 목록
     * @param setAnalysisXIDs 분석 결과 업데이트 함수
     */
    const updatePOIListFromFeatures = (features: any[], setAnalysisXIDs: (xids: number[] | undefined) => void): number[] | undefined => {
        // XID 추출
        const xids: number[] = features
            .map((feature) => {
                const id = feature.id || feature.getId?.();
                if (!id) return null;
                const parts = id.toString().split(".");
                return Number(parts[parts.length - 1]);
            })
            .filter((xid) => xid !== null) as number[];

        // POI 리스트 업데이트
        setAnalysisXIDs(xids);
        return xids;
    };

    /**
     * 지도 오버랩 기능 - 오버랩 지도 <-> 기존 지도 전환 함수
     * mapscreen의 selectedMapIdx가 바뀌면서 map의 useEffect에 의해 실행
     * switchOverlap()은 기본적으로 레이어의 투명도, z-index 조절을 하는 스타일 지정의 영역
     * @param map {@link OlMap} Ol 지도 객체
     * @param overlapMapID 오버랩 지도로 전환될 경우 전달되는 오버랩 지도의 mapId
     */
    const switchOverlap = (map: OlMap, overlapMapID?: string) => {
        const clusterLayer = getLayer(map, LayerName.CLUSTER); // 기존 지도
        const overlapLayerGroup = getLayerGroup(map, LayerName.OVERLAP); // 오버랩 지도 레이어 그룹

        if (!clusterLayer || !overlapLayerGroup) {
            console.log("@@@@@@@@ [MAP] switchOverlap() - clusterLayer 또는 overlapLayerGroup이 없음");
            return;
        }

        const currentLayers = overlapLayerGroup.getLayersArray();
        console.log(`@@@@@@@@ [MAP] switchOverlap() 호출 - overlapMapID: ${overlapMapID}, 현재 레이어 수: ${currentLayers.length}`);
        console.log(
            "     >>> 현재 레이어들: ",
            currentLayers.map((layer) => ({
                mapid: layer.get("mapid"),
                opacity: layer.getOpacity(),
                zIndex: layer.getZIndex(),
            }))
        );

        // 25.09.11 | 오버랩 지도가 있는데 레이어가 아직 생성되지 않은 경우 잠시 후 재시도
        if (overlapMapID && overlapMapIDs) {
            const targetLayer = currentLayers.find((layer) => layer.get("mapid") === overlapMapID);

            if (!targetLayer) {
                console.log(`@@@@@@@@ [MAP] switchOverlap() - 타겟 레이어 ${overlapMapID}가 아직 생성되지 않음, 100ms 후 재시도`);
                setTimeout(() => switchOverlap(map, overlapMapID), 100);
                return;
            }
        }

        if (overlapMapID) {
            console.log(`@@@@@@@@ [MAP] [OVERLAP_SWITCH] 오버랩지도로 전환, overlapMapID: ${overlapMapID}`);

            // 기존 지도를 반투명하게 설정
            clusterLayer.setOpacity(0.5);
            clusterLayer.setZIndex(1);
            clusterLayer.set("interactive", true);
            console.log("     >>> 기본 지도 반투명 설정 완료");

            // 모든 오버랩 지도를 먼저 반투명하게 설정
            currentLayers.forEach((layer) => {
                const layerMapId = layer.get("mapid");
                console.log(`     >>> 오버랩 지도 ${layerMapId} 반투명 설정`);
                layer.setOpacity(0.5);
                layer.setZIndex(1);
                layer.set("interactive", false);
            });

            // 선택된 오버랩 지도만 활성화 설정
            let foundTarget = false;
            currentLayers.forEach((layer) => {
                if (layer.get("mapid") === overlapMapID) {
                    console.log(`@@@@ [MAP] [OVERLAP_SWITCH] 선택된 오버랩 지도 활성화, overlapMapID: ${overlapMapID}`);
                    layer.setOpacity(1);
                    layer.setZIndex(2);
                    layer.set("interactive", true);
                    foundTarget = true;
                }
            });

            if (!foundTarget) {
                console.log(`@@@@@@@@ [MAP] switchOverlap() - 경고: 타겟 레이어 ${overlapMapID}를 찾을 수 없음`);
            }
        } else {
            console.log("@@@@@@@@ [MAP] [OVERLAP_SWITCH] 기존지도로 전환");

            clusterLayer.setOpacity(1); // 기존지도 활성화
            clusterLayer.setZIndex(2);
            clusterLayer.set("interactive", true);

            // 기존지도 외 오버랩 지도 투명도 등 스타일 변경
            currentLayers.forEach((layer) => {
                layer.setOpacity(0.5);
                layer.setZIndex(1);
                layer.set("interactive", false);
            });
        }

        // 결과 로그
        console.log("@@@@@@@@ [MAP] switchOverlap() 완료 후 상태:");
        console.log(`     >>> 기본 지도 opacity: ${clusterLayer.getOpacity()}, zIndex: ${clusterLayer.getZIndex()}`);
        currentLayers.forEach((layer) => {
            console.log(`     >>> 오버랩 지도 ${layer.get("mapid")} opacity: ${layer.getOpacity()}, zIndex: ${layer.getZIndex()}`);
        });
    };

    /**
     * 시간별 이동거리 시작 함수
     * @author smyun
     * @since 2025-01-08 17:00:06
     * @param map {@link olMap} ol 지도 객체
     *        isStart
     */
    const startDirection = (map: OlMap) => {
        const layerD = LayerName.DIRECTION;

        const layerGroup = getLayerGroup(map, layerD);

        // console.log("[DBG] startDirection layerGroup: ", layerGroup);
        const layerS = getLayer(layerGroup!, LayerName.STARTPOINT);
        const source = layerS!.getSource() as VectorSource;

        layerGroup!.setVisible(true);

        let tip = "시작점을 클릭해주세요";

        let [draw, modify] = [
            new Draw({
                source: source,
                type: "Point",
                style: (feature): Style[] => {
                    return directionStyleFunction(draw, map, feature, tip, false);
                },
            }),
            new Modify({ source }),
        ];

        draw.setProperties({ gName: layerD, name: layerS });
        modify.setProperties({ name: layerS });

        draw.on("drawstart", function () {
            source.clear();
            props.setAnalysisStage(-1); //  분석 단계 설정
        });

        draw.on("drawend", function (e) {
            const geometry = e.feature.getGeometry() as Point;
            const coords = geometry.getCoordinates();

            props.setAnalysisStage(Stage.STEP1); //STARTPOINT

            // 좌표계 EPSG:5179 or 3857에서 -> graphhoper 호환 - WGS84 경/위도, EPSG:4326로 변경
            const mapProj = map.getView().getProjection().getCode();

            const transformCoord = transform(coords, mapProj, "EPSG:4326");
            props.setDirectionStartPoint(transformCoord); // number[] 입력

            const styles = directionStyleFunction(draw, map, e.feature as FeatureLike, tip, true);
            e.feature.setStyle(styles);

            map.removeInteraction(draw); // draw Interaction 제거

            // TODO 클릭 시 draw 제거, but, modify는 되는 상태
        });

        map.addInteraction(draw);
    };

    /**
     * 접근성 분석 기능 시작 함수
     * @param map {@link OlMap} Ol 지도 객체
     */
    const startAccessibility = (map: OlMap) => {
        props.setResetAcc(false);

        const name = LayerName.ACCESSIBILITY;
        const layer = getLayer(map, name) as VectorLayer<VectorSource>;
        const source = layer?.getSource() as VectorSource;
        if (!layer || !source) return;

        const [draw, modify, select, overlay] = [
            new Draw({
                source: source,
                type: "Point",
                style: accessibilityMarkerStyle, // 마커 스타일 적용
            }),
            new Modify({
                source: source,
                style: accessibilityMarkerStyle, // 마커 스타일 적용
            }),
            new Select({
                layers: [layer],
                style: accessibilityMarkerStyle, // 마커 스타일 적용
            }),
            new Overlay({
                offset: [8, -2],
                positioning: "bottom-left",
            }),
        ];

        draw.setProperties({ name });
        modify.setProperties({ name });
        select.setProperties({ name });
        overlay.setProperties({ name });

        let lastAddedFeature: OlFeature<Geometry> | null = null; // 마지막 마커 피처
        // 마커 추가
        source.on("addfeature", (e: VectorSourceEvent<Geometry>) => {
            const feature = e.feature;
            if (feature && feature.getGeometry()?.getType() === "Point") {
                feature.setStyle(accessibilityMarkerStyle);
                lastAddedFeature = feature; // 마지막 추가된 마커 저장
            }

            onAddFeature(e, map, draw);
        });

        // 마커 제거
        source.on("removefeature", (e: VectorSourceEvent<Geometry>) => onRemoveFeature(e, map, [draw, modify, select]));

        // 마커 수정
        modify.on("modifyend", (event) => {
            const features = event.features.getArray();

            features.forEach((feature) => {
                //가장 마지막으로 생긴 마커가 아닐 경우 overlay(제거버튼) 생성 안함
                if (
                    feature === lastAddedFeature && // 가장 최근에 추가된 마커인지 확인
                    feature.getGeometry()?.getType() === "Point"
                ) {
                    const coordinates = (feature.getGeometry() as Point).getCoordinates();
                    overlay.setPosition(coordinates); // 오버레이 이동
                }
            });

            setAccState(source.getFeatures());
            select.getFeatures().on("add", (e: CollectionEvent<OlFeature<Geometry>>) => onSelectFeature(e, map, source, overlay));
        });

        select.getFeatures().on("add", (e: CollectionEvent<OlFeature<Geometry>>) => onSelectFeature(e, map, source, overlay));

        layer.setVisible(true);

        map.addInteraction(draw);
        map.addInteraction(modify);
        map.addInteraction(select);

        map.getViewport().style.cursor = "pointer";
    };

    /**
     * 지도 오버랩 기능 시작 함수
     * @param map {@link OlMap} OL map 객체
     * @param overlapMapIds string[] 오버랩할 지도 맵 아이디 목록
     */
    // !@#!-OA1-M4-USE1>METHOD1. 오버랩 지도 시작 함수 - 클러스터 레이어 Z-Index 조정 및 지도 데이터 요청
    const startOverlapMap = async (map: OlMap, overlapMapId: string) => {
        const clusterLayer = getLayer(map, LayerName.CLUSTER);
        clusterLayer?.setZIndex(2);

        console.log("@@@@@@@@@@@@@ startOverlapMap 시작: ", overlapMapId);
        try {
            await reqMapData(overlapMapId, map, "overlap");
            // console.log("@@@@@@@@@@@@@ startOverlapMap reqMapData 성공 완료: ", overlapMapId);
        } catch (error) {
            console.error("@@@@@@@@@@@@@ startOverlapMap reqMapData 에러: ", overlapMapId, error);
            throw error;
        }
    };

    // 데이터 호출 API  -----------------------------------------------------------
    const reqMapInfoData = async (mapid: string, properties?: MapInfoOptions[]) => {
        const response = await DataProvider.GetMapInfo({ mapid }, properties ? { properties } : undefined);
        if (!response.flag || !response.result) return;
        return response.result as MapInfo;
    };

    // !@#!-OA1-M4-USE1>METHOD1>METHOD1>METHOD1. 지도 메타 데이터 비동기 요청 함수 - 메타데이터 조회
    const reqMetadata = async (mapid: string) => {
        const response = await DataProvider.GetMetadata({ mapid });

        if (response.flag) {
            return response.result;
        } else {
            // 지도가 없을 경우
            response.status && response.status === 404 && props.movePage(undefined, { replace: true });
        }
    };

    /**
     * 지도 id를 가지고 지도 데이터 비동기 요청 함수 - 메타데이터 조회 및 레이어 생성
     * @param mapid 지도 id
     * @param map OL map 객체
     * @param type 요청 타입 ( type이 overlap으로 넘어올 경우 오버랩 데이터 호출 )
     * @param doFit 데이터 로드 후 지도 화면을 전체 영역에 맞출지 여부 (새로고침 시 줌 유지를 위해 false 사용 가능)
     */
    const reqMapData = async (mapid: string, map: OlMap, type: "map" | "overlap", doFit: boolean = true) => {
        // 25.09.11 | 오버랩 지도 등록 시작 시 추적 목록에 추가
        if (type === "overlap") {
            registeringOverlapMapIds.current.add(mapid);
        }

        console.log("@@@@@@ reqMapData called reqMetadata: ", mapid);
        const _metaData = await reqMetadata(mapid);

        if (type === "map" && !metaData) setMetaData(_metaData); // 기존 지도만 metadata(state) 설정

        if (!_metaData) return;

        let response = await DataProvider.GetPlaceCount({ mapid });

        if (!response.flag || !response.result) return;

        const count = response.result.count as number;
        const pages = Math.ceil((count / GET_FEATURE_UNIT) as number);

        // console.log(`@@@@@@@@@@@@@ [Map] reqMapData - mapid: ${mapid}, type: ${type}, count: ${count}, pages: ${pages}`);

        if (type === "map") {
            for (let page = 1; page <= pages; page++) {
                const status = { ...getfeatureStatus.current };
                status[page] = false;
                getfeatureStatus.current = status;

                const clusterLayer = getLayer(map, LayerName.CLUSTER) as ClusterLayer;

                if (clusterLayer) {
                    // 클러스터 레이어의 스타일을 명시적으로 재설정
                    clusterLayer.setStyle(undefined);
                    // 기존 인터랙션 제거
                    removeInteractions(map, LayerName.CLUSTER);
                }

                reqFeatureData(mapid, _metaData).then((fc) => {
                    const status = { ...getfeatureStatus.current };

                    if (fc) {
                        status[page] = true;
                    } else {
                        delete status[page];
                    }

                    getfeatureStatus.current = status;

                    fc && setFeatures(map, fc, mapid, false, doFit);
                });
            }
        }
        // 오버랩 지도 데이터 설정
        else if (type === "overlap") {
            // console.log(`@@@@@@@@@@@@@ [Map] reqMapData - 오버랩 타입 처리 시작: ${mapid}`);
            const mapInfo = await reqMapInfoData(mapid, ["category", "famousMarker"]);

            if (!mapInfo) {
                // console.log(`@@@@@@@@@@@@@ [Map] reqMapData - mapInfo 없음, 종료: ${mapid}`);
                return;
            }
            // console.log(`@@@@@@@@@@@@@ [Map] reqMapData - mapInfo 수신 완료: ${mapid}`);

            for (let page = 1; page <= pages; page++) {
                const status = { ...getfeatureStatus.current };
                status[page] = false;
                getfeatureStatus.current = status;

                // console.log(`@@@@@@@@@@@@@ [Map] reqMapData - 오버랩 지도 ${mapid} 페이지 ${page} 피처 데이터 요청 시작`);
                try {
                    const fc = await reqFeatureData(mapid, _metaData);
                    // console.log(`@@@@@@@@@@@@@ [Map] reqMapData - 오버랩 지도 ${mapid} 페이지 ${page} 피처 데이터 수신 완료`);

                    const statusAfter = { ...getfeatureStatus.current };

                    if (fc) {
                        statusAfter[page] = true;
                    } else {
                        delete statusAfter[page];
                    }

                    getfeatureStatus.current = statusAfter;

                    // console.log("@@@@@@@@@@@@@ [Map] 이건 reqMapData에서 도는 setOverlapFeatures 입니다.")
                    if (fc) {
                        // console.log(`@@@@@@@@@@@@@ [Map] reqMapData - setOverlapFeatures 호출 시작: ${mapid}`);
                        setOverlapFeatures(map, fc, mapid, mapInfo, _metaData.metadata);
                        // console.log(`@@@@@@@@@@@@@ [Map] reqMapData - setOverlapFeatures 호출 완료: ${mapid}`);
                        // 25.09.11 | 오버랩 지도 등록 완료 시 추적 목록에서 제거
                        setTimeout(() => {
                            registeringOverlapMapIds.current.delete(mapid);
                            highlightManager.clearAllSelections();
                        }, 1000);
                    }
                } catch (error) {
                    // console.error(`@@@@@@@@@@@@@ [Map] reqMapData - 페이지 ${page} 피처 데이터 요청 실패:`, error);
                    // 에러 발생해도 상태는 업데이트
                    const statusAfter = { ...getfeatureStatus.current };
                    delete statusAfter[page];
                    getfeatureStatus.current = statusAfter;
                }
            }
        }

        // console.log(`@@@@@@@@@@@@@ [Map] reqMapData 함수 완전 종료: ${mapid}, type: ${type}`);
    };

    // 피처 데이터 호출 API
    // !@#!-OA1-M4-USE1>METHOD1>METHOD1>METHOD1. 오버랩 지도 id를 가지고 지도 feature 대이터를 비동기 요청 함수 - GetFeature,
    const reqFeatureData = async (mapid: string, metaData: MetaData, categoryLabel?: string) => {
        const response = await DataProvider.GetFeatureList(
            { mapid },
            {
                page: 1,
                count: 50000,
                ...(categoryLabel && categoryLabel != "전체" && { filter: { category: categoryLabel } }),
            }
        );

        if (!response.flag || !response.result) return;

        const geojson = response.result as FeatureCollection;

        geojson.features = convertFeatureProperties(geojson.features, metaData);

        return geojson;
    };

    const convertFeatureProperties = (features: Feature[], metaData: MetaData): Feature[] => {
        const { category, placeName, geoField } = metaData.metadata;

        return features.map((f) => {
            const props = f.properties as { [key: string]: any } | undefined;
            if (!props) return f;

            if (category) {
                props.category = props[category] ?? null;
                delete props[category];
            } else {
                props.category = null;
            }

            props.label = props[placeName] ?? "";
            delete props[placeName];

            props.geoField = props[geoField];
            delete props[geoField];

            return f;
        });
        
    };

    const reqRegionPolygons = async (type: RegionType, map: OlMap) => {
        if (type === RegionType.LI) return; // 리 단위 생략

        const response = await DataProvider.GetRegionPolygons(type);
        const geojson = response.result;

        if (!response.flag || !geojson) {
            // TODO mjkang 예외 처리
            return;
        }

        const layers = map.getAllLayers().filter((layer) => layer.get("name") == type);

        layers.forEach((layer) => {
            const source = layer.getSource() as VectorSource;

            if (source) {
                const features = new GeoJSON().readFeatures(geojson, { featureProjection: map.getView().getProjection() });
                features && source.addFeatures(features as OlFeature[]);
            }
        });
    };

    // 25.07.07 | Kim Young Jun | 분석 기능 매트릭 데이터 캐싱 추가
    let metricValueCache: Record<string, number> = {};

    const reqAnalysisData = async (
        map: OlMap,
        mapid: string,
        upperRegion: string,
        name: string,
        isStart?: boolean,
        upperElement?: OlFeature | BaseLayer,
        analysisMetricRef?: number
    ) => {
        metricValueCache = {}; // 캐시 초기화
        let regionType: RegionType | undefined;

        if (name === LayerName.CHART && isStart) {
            regionType = getRegionType(upperRegion);
        } else {
            regionType = getRegionType(upperRegion, "lower");
        }

        if (!regionType) return;

        const layerGroup = getLayerGroup(map, name);
        const layer = layerGroup?.getLayersArray().find((l) => l.get("name") === regionType) as VectorLayer<VectorSource>;
        const clusterLayer = getLayer(map, LayerName.CLUSTER);

        if (!layer) return;

        try {
            const source = layer.getSource() as VectorSource;
            const clusterSource = clusterLayer?.get("source") as ClusterSource;
            const featureSource = clusterSource?.getSource() as VectorSource;

            const response = await DataProvider.GetRegionAnalysisData(
                { mapid },
                {
                    type: 0,
                    upperRegion: upperRegion,
                }
            );

            // 25.07.04 | Kim Young Jun | API 응답 없을 때 처리를 위해 수정
            let data: RegionAnalysisData[] = [];
            let rawLegend: RegionAnalysisLegend[] = [];

            if (response.flag && response.result) {
                const result = response.result;
                data = result.data;
                rawLegend = result.legend;
            }

            let caledLegend = rawLegend;

            // 확장 메트릭일 경우 클라이언트에서 범례 계산
            if (analysisMetricRef !== 21) {
                // 1. 모든 피처의 메트릭 값을 한 번만 계산하여 캐시
                const features = source.getFeatures();
                const metricValues: number[] = [];

                // 25.08.01 | Kim Seong Min | KBS 위해 예외 추가 (지워야 함)
                const fake = regionType == RegionType.CTPRVN ? true : false;

                for (const feature of features) {
                    //upperRegion
                    const geom = feature.getGeometry();
                    const regionCode = feature.get(`${regionType}_CD`) as string;

                    if (upperRegion !== "0" && !regionCode.startsWith(upperRegion)) {
                        // console.log("[DBG] reqAnalysisData regionCode: ", regionCode, " / ", "upperRegion: ", upperRegion);
                        continue;
                    }

                    if (!geom) {
                        metricValueCache[regionCode] = 0;
                        metricValues.push(0);
                        continue;
                    }

                    // 메트릭 값 계산 및 캐싱

                    // 25.08.01 | Kim Seong Min | KBS 위해 예외 추가 (지워야 함)
                    const value = setMetricDataByGeometry(geom, featureSource, fake, regionCode);
                    metricValueCache[regionCode] = value;
                    metricValues.push(value);
                }

                // console.log("[DBG] metricValues:", metricValues);

                // 2. Jenks 자연 분류법으로 범례 생성
                caledLegend = generateJenksLegend(metricValues);
                // console.log("[DBG] Jenks legend generated:", caledLegend);
            }

            source.getFeatures().forEach((feature) => {
                const regionCode = feature.get(`${regionType}_CD`) as string;

                // 필터링 로직
                if (name === LayerName.CHART && isStart) {
                    // 2026.01.16 | dslee | Type Error 방어코드 추가
                    if (data && data.length > 0 && data[0]) {
                        data[0].region = "0";
                    }

                    if (regionType !== RegionType.COUNTRY && !regionCode.startsWith(upperRegion)) {
                        feature.unset("focus");
                        return;
                    }
                } else {
                    if (regionType !== RegionType.CTPRVN && !regionCode.startsWith(upperRegion)) {
                        feature.unset("focus");
                        return;
                    }
                }

                let count = 0;
                let cls = 0;

                // 메트릭 계산 - 캐시된 값 사용
                if (analysisMetricRef === 21) {
                    const item = data.find((item) => item.region === regionCode);
                    count = item?.count ?? 0;

                    const match = caledLegend.find((l) => (l.min <= count && l.max > count) || (l.min === l.max && count === l.max));
                    cls = match?.class ?? 0;
                } else {
                    // 캐시된 메트릭 값 사용 (중복 계산 방지)
                    count = metricValueCache[regionCode] ?? 0;
                    cls = classifyValue(count, caledLegend);
                }

                // 속성 및 스타일 설정
                feature.setProperties({
                    count,
                    class: cls,
                    numOfCls: caledLegend.length,
                    focus: true,
                });

                const styleMap: Record<string, StyleLike> = {
                    [LayerName.REGION]: regionStyleFunction,
                    [LayerName.CHOROPLETH]: choroplethStyleFunction,
                    [LayerName.CHART]: chartStyleFunction,
                };

                const styleFn = styleMap[name] ?? choroplethStyleFunction;
                feature.setStyle(styleFn);

                // if (!feature.getStyle()) console.warn("Style not applied on feature:", feature.getProperties());
            });

            props.setLegend?.(caledLegend);
            legendData[upperRegion] = caledLegend;

            upperElement &&
                upperElement.setProperties({
                    hasData: true,
                    hasDataMetricIdx: analysisMetricRef,
                });
        } catch (error) {
            console.error("Error in reqAnalysisData:", error);
        }
    };

    /**
     * 지역에 대한 메트릭 데이터를 설정하는 함수
     * 25.07.07 | Kim Young Jun | 성능 개선 진행
     */
    const setMetricDataByGeometry = (geometry: Geometry, featureSource: VectorSource, fake: boolean, regionCode: any) => {
        // 1. 캐시 키 생성 (지오메트리 ID 또는 해시)
        const geomId = geometry.getExtent().join(",");
        const metricIdx = analysisMetricRef.current;
        const cacheKey = `${geomId}_${metricIdx}_${selectedDropdownFieldRef.current}`;
        // 2. 캐시된 결과가 있으면 즉시 반환
        if (metricValueCache[cacheKey]) {
            return metricValueCache[cacheKey]!;
        }

        // 3. 공간 인덱싱을 통한 후보 피처 필터링
        // let startTime = performance.now();
        const extent = geometry.getExtent();
        const candidateFeatures = featureSource.getFeaturesInExtent(extent);

        // console.log(`[PERF] getFeaturesInExtent took ${(performance.now() - startTime).toFixed(2)}ms for ${candidateFeatures.length} features`);

        // 4. 메트릭 합계 계산 (일괄 처리)
        // startTime = performance.now();

        let sum = 0;

        // 5. 메트릭 계산 함수 - 캐시 활용 최적화
        const getMetricValueCached = (xid: number) => {
            const metricCacheKey = `${xid}_${metricIdx}_${selectedDropdownFieldRef.current}`;

            if (metricValueCache[metricCacheKey]) {
                return metricValueCache[metricCacheKey]!;
            }

            const placeInfoSet = allPlaceDataRef.current[xid];
            if (!placeInfoSet) return 0;

            let value = 0;

            if (metricIdx === 22) value = placeInfoSet.primaryinfo?.thumbCnt ?? 0;
            else if (metricIdx === 23) value = placeInfoSet.primaryinfo?.viewCnt ?? 0;
            else if (metricIdx === 24) value = placeInfoSet.primaryinfo?.storyCnt ?? 0;
            else {
                const selectedField = selectedDropdownFieldRef.current;
                if (!selectedField) return 0;

                const rawVal = placeInfoSet.placeInfo[selectedField];
                if (typeof rawVal === "number") value = rawVal;
                else if (typeof rawVal === "string") {
                    const cleaned = rawVal.replace(/,/g, "");
                    const num = Number(cleaned);
                    value = isNaN(num) ? 0 : num;
                }
            }

            // 메트릭 값 캐싱
            metricValueCache[metricCacheKey] = value;
            return value;
        };

        // 6. 배치 처리 - 피처를 작은 그룹으로 나눠 처리
        const batchSize = 1000;

        for (let i = 0; i < candidateFeatures.length; i += batchSize) {
            const batch = candidateFeatures.slice(i, Math.min(i + batchSize, candidateFeatures.length));

            // 실제 교차 계산은 이 지점에서 수행
            for (const place of batch) {
                const placeGeom = place.getGeometry() as Point;
                const coords = placeGeom.getCoordinates();

                // 공간적 교차 검사 - 캐시 가능한 부분
                if (geometry.intersectsCoordinate(coords)) {
                    const id = place.getId() as string;
                    const xid = Number(id.split(".")[1]);
                    const value = getMetricValueCached(xid);
                    sum += value;
                }
            }
        }

        // console.log(`[PERF] metric calculation took ${(performance.now() - startTime).toFixed(2)}ms for ${candidateFeatures.length} features`);

        if (fake) {
            const _index = cacheKey.lastIndexOf("_");
            if (!isNaN(Number(cacheKey.charAt(_index - 1)))) {
                const fieldVal = cacheKey.slice(_index + 1);
                const val = getFakeMetricValue(regionCode, fieldVal);
                // console.log(val);
                const res = val == -1 ? sum : val;
                metricValueCache[cacheKey] = res;
                return res;
            } else {
                metricValueCache[cacheKey] = sum;
            }
        } else {
            // 7. 최종 결과 캐싱
            metricValueCache[cacheKey] = sum;
        }

        return sum;
    };

    // KBS 관련
    // SUM으로 계산된 값이 아닌 임의의 값을 넣기 위해 만든 함수. 나중에 지울 것
    const getFakeMetricValue = (regionCode: number, fieldName: string): number => {
        // 강원도 51
        if (regionCode == 51) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.989;
                case "조선귀족":
                    return 0.6;
                case "일본인":
                    return 1.6;
                case "법인":
                    return 0.1;
            }
        }

        // 경기도 41
        else if (regionCode == 41) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.404;
                case "조선귀족":
                    return 1.4; // 예시 값
                case "일본인":
                    return 1.9; // 예시 값
                case "법인":
                    return 0.5;
            }
        }
        // 서울 11
        else if (regionCode == 11) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.078;
                case "조선귀족":
                    return 2.5; // 예시 값
                case "일본인":
                    return 7.4; // 예시 값
                case "법인":
                    return 4.5;
            }
        }
        // 충청남도 44
        else if (regionCode == 44) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.561;
                case "조선귀족":
                    return 1.0; // 예시 값
                case "일본인":
                    return 3.1; // 예시 값
                case "법인":
                    return 1.3;
            }
        }
        // 충청북도 43
        else if (regionCode == 43) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.634;
                case "조선귀족":
                    return 1.2; // 예시 값
                case "일본인":
                    return 1.6; // 예시 값
                case "법인":
                    return 0.4;
            }
        }
        // 전북 52
        else if (regionCode == 52) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.966;
                case "조선귀족":
                    return 0.5; // 예시 값
                case "일본인":
                    return 7.1; // 예시 값
                case "법인":
                    return 1.9;
            }
        }
        // 세종
        else if (regionCode == 36) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.349;
                case "조선귀족":
                    return 1.4; // 예시 값
                case "일본인":
                    return 5.7; // 예시 값
                case "법인":
                    return 0.3;
            }
        }
        // 대전
        else if (regionCode == 30) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.987;
                case "조선귀족":
                    return 1.7; // 예시 값
                case "일본인":
                    return 6.4; // 예시 값
                case "법인":
                    return 2.6;
            }
        }

        // 인천
        else if (regionCode == 28) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.177;
                case "조선귀족":
                    return 0.2; // 예시 값
                case "일본인":
                    return 3.8; // 예시 값
                case "법인":
                    return 0.0;
            }
        }

        // 전남 46
        else if (regionCode == 46) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.98;
                case "조선귀족":
                    return 0.6; // 예시 값
                case "일본인":
                    return 6.5; // 예시 값
                case "법인":
                    return 2.9;
            }
        }

        // 광주 29
        else if (regionCode == 29) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.979;
                case "조선귀족":
                    return 0.4; // 예시 값
                case "일본인":
                    return 8.4; // 예시 값
                case "법인":
                    return 2.4;
            }
        }

        // 경북 47
        else if (regionCode == 47) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.622;
                case "조선귀족":
                    return 0.5; // 예시 값
                case "일본인":
                    return 1.2; // 예시 값
                case "법인":
                    return 0.5;
            }
        }

        //경남 48
        else if (regionCode == 48) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.751;
                case "조선귀족":
                    return 0.4; // 예시 값
                case "일본인":
                    return 2.1; // 예시 값
                case "법인":
                    return 0.7;
            }
        }
        // 대구 27
        else if (regionCode == 27) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.062;
                case "조선귀족":
                    return 0.5; // 예시 값
                case "일본인":
                    return 1.4; // 예시 값
                case "법인":
                    return 0.3;
            }
        }

        // 부산 26
        else if (regionCode == 26) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.292;
                case "조선귀족":
                    return 0.3; // 예시 값
                case "일본인":
                    return 8.6; // 예시 값
                case "법인":
                    return 0.7;
            }
        }

        // 울산 31
        else if (regionCode == 31) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.548;
                case "조선귀족":
                    return 0.4; // 예시 값
                case "일본인":
                    return 6.3; // 예시 값
                case "법인":
                    return 2.1;
            }
        }

        // 제주 50
        else if (regionCode == 50) {
            switch (fieldName) {
                case "[식별가능] 비율(J/I)":
                    return 0.992;
                case "조선귀족":
                    return 0.2; // 예시 값
                case "일본인":
                    return 0.4; // 예시 값
                case "법인":
                    return 0.0;
            }
        }

        return -1; //
    };

    const reqAccessibilityData = async (map: OlMap, vehicle: TransportMode) => {
        const name = LayerName.ACCESSIBILITY;
        const layer = getLayer(map, name);
        const source = layer?.getSource() as VectorSource;
        if (!layer || !source) return;

        // setIsLoading(true);

        const markerCoords: [number, number][] = []; // 선택된 마커 좌표
        const promises = source.getFeatures().map((feature) => {
            const point = feature.getGeometry() as Point;
            const [lon, lat] = toLonLat(point.getCoordinates(), map.getView().getProjection());

            markerCoords.push([lon, lat]);
            return DataProvider.GetAccessibilityData({
                vehicle: vehicle,
                point: [lon, lat],
            });
        });

        const responses = await Promise.all(promises);
        const polygons: Feature<Polygon, GeoJsonProperties>[] = [];

        responses.forEach((response) => {
            if (!response.flag || !response.result) return;
            const result = response.result as Feature<Polygon, GeoJsonProperties>[];
            polygons.push(...result);
        });

        removeOverlays(map, name);
        removeInteractions(map, name);

        for (let i = 0; i < accessibilityCondition.BUCKETS; i++) {
            let intersection: Feature<Polygon | MultiPolygon, GeoJsonProperties> | null = null;

            const _polygons = polygons.filter((f) => {
                const properties = f.properties as { [key: string]: number };
                return properties && properties["bucket"] === i;
            });

            for (let j = 0; j < _polygons.length; j++) {
                if (j === 0) {
                    intersection = _polygons[j];
                    continue;
                }

                if (!intersection) break;

                intersection = turfIntersect(turfFeatureCollection([intersection, _polygons[j]]));
            }

            if (intersection) {
                // 선택된 마커가 교집합 폴리곤에 포함 여부 체크
                const turfPolygon = intersection;
                const isAnyMarkerInside = markerCoords.some((coord) => {
                    const marker = turfPoint(coord);
                    return booleanPointInPolygon(marker, turfPolygon);
                });

                if (isAnyMarkerInside) {
                     if (props.postMessage) {
                        props.postMessage?.({ type: 'WEB_VIEW_MESSAGE', payload: "선택된 위치들이 너무 가까워요. 좀 더 먼 거리의 지점을 선택해 주세요." });
                    } else {
                        showModal("선택된 위치들이 너무 가까워요. 좀 더 먼 거리의 지점을 선택해 주세요.");
                    }
                    props.setResetAcc(true);
                    return;
                }

                // 원본 폴리곤
                _polygons.forEach((poly) => {
                    const original = new GeoJSON().readFeature(poly, {
                        dataProjection: accessibilityCondition.PROJECTION,
                        featureProjection: map.getView().getProjection(),
                    });
                    original.setStyle(accessibilityRawPolygonStyle);
                    source.addFeature(original);
                });

                const feature = new GeoJSON().readFeature(intersection, {
                    dataProjection: accessibilityCondition.PROJECTION,
                    featureProjection: map.getView().getProjection(),
                });

                // 교집합 폴리곤
                feature.setStyle(accessibilityPolygonStyle);
                source.addFeature(feature);

                props.setAccState();
                // console.log("버킷: ", i, " / 걸린 시간 : ", (30 / accessibilityCondition.BUCKETS) * (i + 1), "분")

                return;
            } else if (i == accessibilityCondition.BUCKETS - 1) {
                // 최대 버킷에 도달할 때 까지 범위가 없는 경우
                if (props.postMessage) {
                    props.postMessage?.({ type: 'WEB_VIEW_MESSAGE', payload: "모든 분석 대상 지점으로부터 동일 시간(최대 30분) 내 접근 가능한 범위가 없습니다." });
                } else {
                    showModal("모든 분석 대상 지점으로부터 동일 시간(최대 30분) 내 접근 가능한 범위가 없습니다.");
                }
                props.setResetAcc(true);
            }
        }
    };

    const reqRouteData = async () => {
        if (!mapObj) return;

        const layer = getLayer(mapObj, LayerName.CLUSTER);
        const source = layer?.get("source") as ClusterSource;
        const vectorSource = source.getSource();

        if (!layer || !source || !vectorSource) return;

        const feature = vectorSource.getFeatureById(`${baseMapID}.${props.selectedPoiIdx}`); // 선택된 장소 좌표

        if (feature) {
            const geometry = feature.get("geometry");
            const coordinates = geometry?.getCoordinates();
            const mapProj = mapObj.getView().getProjection().getCode();
            const endPoint = transform(coordinates, mapProj, "EPSG:4326");
            const startPoint = props.startPoint;

            try {
                // 길찾기 데이터
                const getRouteData = await DataProvider.GetRouteData({
                    point1: startPoint!,
                    point2: endPoint,
                    vehicle: props.directionVehicle!,
                });
                getRouteData && drawRouteMultiLine(mapObj, getRouteData, startPoint, endPoint);
            } catch (error) {
                showModal("이동경로 분석에 실패하였습니다.");
            }
        }
    };

    // 이벤트 핸들러 EventHandlers -----------------------------------------------------------
    const reqRevGeocoding = async (coords: Coordinate) => {
        const coordString = coords.join(",");

        const revGeocoding = await DataProvider.RevGeocoding({ coord: coordString });
        if (revGeocoding.flag) {
            const address = revGeocoding.result.road_address;
            if (address != "Not found") {
                props.setIsOpenAgreeModal(false);
                props.setNewPlaceAddress(address);
            } else {
                props.setNewPlaceAddress("");
                props.setIsOpenAgreeModal(true);
                props.setAgreeNoneAddress(true);
            }
        } else {
            showModal("마커의 위치를 다시 이동시켜주세요.");
        }

        props.setRevGeoLoading(false);
    };

    const updateMarkerPosition = (coord: Coordinate) => {
        if (!mapObj) return;

        if (props.isAddMode && sideSheetContentRef.current === ContentList.EditPlace) {
            // 편집 중인 마커의 임시 좌표 업데이트
            const featureId = `${baseMapID}.${selectedPoiIdxRef.current}`;
            highlightManager.updateTemporaryCoordinates(coord);

            // 기존 장소 위치 변경 시 마커의 좌표값 변경
            const center = mapObj.getView().getCenter();
            const clusterLayer = getLayer(mapObj, LayerName.CLUSTER);
            const clusterSource = clusterLayer?.get("source") as ClusterSource;
            const vectorSource = clusterSource?.getSource();

            if (!clusterLayer || !clusterSource || !center || !vectorSource) return;

            const feature = vectorSource.getFeatureById(featureId);
            if (!feature) return;

            const geometry = feature.getGeometry();
            if (geometry instanceof Point) {
                geometry.setCoordinates(coord); // 기존의 좌표를 바뀐 좌표로 변경
            }
        }
    };

    const onTranslateStart = useCallback(
        (e: TranslateEvent) => {
            const coordinates = (e.features.getArray()[0].getGeometry() as Point).getCoordinates();
            translateGeometry.current = e.features.getArray()[0].getGeometry() as Point;
            revertPositionRef.current = coordinates;

            // 편집 시작 시 하이라이트 매니저에 편집 정보 등록
            if (props.isAddMode && props.sideSheetCont === ContentList.EditPlace) {
                const featureId = `${baseMapID}.${selectedPoiIdxRef.current}`;
                highlightManager.startEditingFeature(featureId, coordinates);
            }
        },
        [mapObj, props.isAddMode, props.sideSheetCont, baseMapID, selectedPoiIdxRef.current]
    );

    const onTranslating = useCallback(
        (e: TranslateEvent) => {
            const coordinates = (e.features.getArray()[0].getGeometry() as Point).getCoordinates();
            updateMarkerPosition(coordinates);
        },
        [mapObj]
    );

    const onTranslateEnd = useCallback(
        (e: TranslateEvent) => {
            const coordinates = (e.features.getArray()[0].getGeometry() as Point).getCoordinates();
            saveCoords(coordinates);

            // 좌표값으로 주소정보 가져오기(reverse geocoding)
            props.setRevGeoLoading(true);
            reqRevGeocoding(coordinates);

            // 변경된 좌표값으로 마커 이동
            updateMarkerPosition(coordinates);
        },
        [mapObj]
    );

    useEffect(() => {
        props.isGPSOn && geoLocation();
    }, [props.isGPSOn]);

    // useEffect(() => {
    //     if(!props.newPlaces || props.newPlaces.length == 0) return;

    //     const coord = props.newPlaces[0]["1"].coords;
    //     updateMarkerPosition(coord)
    // }, [props.newPlaces])

    // 2026.01.28 | dslee | GPS 로딩 상태 추가
    const geoLocation = () => {
        if (!mapObj) return;

        props.setIsGPSOn(false);
        if (!navigator.geolocation) {
            alert("Geolocation을 지원하지 않는 브라우저입니다.");
            return;
        }

        // 로딩 시작
        props.setIsGPSLoading(true);
        navigator.geolocation.getCurrentPosition((position) => {
                const lon = position.coords.longitude;
                const lat = position.coords.latitude;

                const projection = mapObj.getView().getProjection();
                const projectionCode = projection.getCode();
                const coords = transform([lon, lat], "EPSG:4326", projectionCode); // EPSG:4326 → EPSG:5179 변환

                // 해당 위치로 지도를 이동 (zoom과 함께)
                mapObj.getView().animate({
                    center: coords,
                    zoom: 7,
                    duration: 500,
                });
                
                // 위치 이동 완료 후 로딩 종료
                props.setIsGPSLoading(false);
            },
            (error) => {
                // 에러 발생 시에도 로딩 종료
                props.setIsGPSLoading(false);
                console.error("GPS 위치 정보를 가져올 수 없습니다:", error);
            }
        );
    };

    const onHoverEnter = useCallback(
        (e: HoverEvent) => {
            const source = e.layer?.getSource() as VectorSource;
            source?.getFeatures().forEach((f) => f.unset("hover")); // 이전 hover 제거

            (e as any).feature.setProperties({ hover: true }); // 현재 hover 설정

            if (mapObj) {
                const overlay = mapObj
                    .getOverlays()
                    .getArray()
                    .find((o) => o.get("name") === LayerName.CHOROPLETH) as Overlay | undefined;

                if (overlay) mapObj.addOverlay(overlay);

                updateOverlayLabel(mapObj, (e as any).feature); // Label 설정
            }
        },
        [mapObj]
    );

    const onHoverLeave = useCallback(
        (group: LayerGroup, map: OlMap) => {
            const layer = group.getLayersArray().find((l) => l.isVisible(map.getView()));
            const source = layer?.getSource() as VectorSource;

            source?.getFeatures().forEach((f) => f.unset("hover")); // 모든 hover 초기화

            if (mapObj) {
                updateOverlayLabel(mapObj, undefined);
                updateOverlayChartLabel(mapObj, undefined);
            }

            setIsOnGraph(false); // 그래프 오버레이 제거
        },
        [mapObj]
    );

    /**
     * 단계구분도, 행정경계, 차트 분석
     */
    const onSelectRegion = useCallback(
        (e: CollectionEvent<OlFeature<Geometry>>, map: OlMap, select: Select) => {
            const region = e.element;
            region.setProperties({ selected: true });

            const layer = select.getLayer(region);

            // 25.07.10 | Kim Young Jun | 선택된 행정구역의 레이어 이름과 코드 추출
            const layerName = layer.get("name") as RegionType;
            const regionCode = region.get(`${layerName}_CD`) as string;

            // 현재 선택된 행정구역 정보 저장
            props.setCurrentRegionCode(regionCode);

            const geometry = region.getGeometry();
            const { min, max } = layer.getProperties();

            if (!geometry) return;

            const extent = geometry.getExtent();
            const scaledExtent = [
                extent[0] + (extent[2] - extent[0]) * 0.05,
                extent[1] + (extent[3] - extent[1]) * 0.05,
                extent[2] - (extent[2] - extent[0]) * 0.05,
                extent[3] - (extent[3] - extent[1]) * 0.05,
            ];

            // 25.07.11 | Kim Young Jun | 모바일 뷰일 때 패딩값 조정
            const padding = props.isMobileView ? -250 : 100;
            // console.log("[DEBUG] onSelectRegion padding:", padding);

            map.getView().fit(scaledExtent, {
                // minResolution: min,
                // maxZoom: maxZoom,
                padding: [padding, padding, padding, padding],
                duration: 500,
            });

            // console.log("[DEBUG] onSelectRegion - region:", region.getProperties(), "geometry:", geometry.getType());

            setTimeout(() => {
                zoomIntoSelectedRegion(select, region);
                if (mapObj) {
                    updateOverlayLabel(mapObj, region); // Label 설정
                }
            }, 600);
        },
        [mapObj]
    );

    const onChartHoverEnter = useCallback(
        (e: HoverEvent) => {
            const region = (e as any).feature;

            const map = e.map;
            const layer = e.layer;

            // features hover 속성 제거
            const source = layer.getSource() as VectorSource;
            source?.getFeatures().forEach((f) => f.unset("hover"));

            region.setProperties({ hover: true });

            const geometry = region.getGeometry();

            if (!geometry) return;

            const key = Object.keys(region.getProperties()).find((k) => k.endsWith("KOR_NM"));
            const regionName = key ? region.get(key) : "선택지역";

            const clusterLayer = getLayer(map, LayerName.CLUSTER);
            const clusterSource = clusterLayer?.get("source") as ClusterSource;
            const featureSource = clusterSource?.getSource() as VectorSource;

            if (!featureSource) return;

            const { labels, values, colors } = getChartDataByGeometry(geometry, featureSource, props.categories);

            if (graphOverlayRef.current && graphOverlay.current) {
                let coordinate: number[] | undefined;

                if (geometry.getType() === "Polygon") {
                    const poly = geometry as OlPolygon;
                    coordinate = poly.getInteriorPoint().getCoordinates();
                } else if (geometry.getType() === "MultiPolygon") {
                    const multi = geometry as OlMultiPolygon;
                    const biggest = multi.getPolygons().sort((a, b) => b.getArea() - a.getArea())[0];
                    coordinate = biggest.getInteriorPoint().getCoordinates();
                }

                if (coordinate) {
                    if (layer.get("name") === RegionType.COUNTRY) {
                        graphOverlay.current.setOffset([-130, 0]);
                        labelOverlay!.current!.setOffset([-130, -130]);
                    } else {
                        graphOverlay.current.setOffset([0, 0]);
                        labelOverlay!.current!.setOffset([0, -130]);
                    }

                    graphOverlay.current.setPosition(coordinate);

                    if (!graphRootRef.current) {
                        graphRootRef.current = ReactDOM.createRoot(graphOverlayRef.current!);
                    }

                    graphRootRef.current.render(<Graph title={regionName} labels={labels} values={values} colors={colors} />);
                }
            }

            setIsOnGraph(true);

            if (mapObj) {
                updateOverlayChartLabel(mapObj, region);
            }
        },
        [mapObj]
    );

    const getChartDataByGeometry = (geometry: Geometry, featureSource: VectorSource, categories: CategoryOptions[]) => {
        const placeFeatures = featureSource.getFeatures();

        const categoryCountMap: Record<string, number> = {};
        const categoryColorMap: Record<string, string> = {};

        // 1. 전체 카테고리 초기화
        const categoryLabels = categories.filter((c) => c.label !== "전체").map((c) => c.label ?? "기타");

        categoryLabels.forEach((label) => {
            const color = categories.find((c) => c.label === label)?.color ?? "#015EFF";
            categoryCountMap[label] = 0;
            categoryColorMap[label] = color;
        });

        const visibleFeatures = placeFeatures.filter((place) => geometry.intersectsCoordinate((place.getGeometry() as Point).getCoordinates()));

        visibleFeatures.forEach((place) => {
            const cat = (place.get("category") as string) ?? "기타";
            categoryCountMap[cat] = (categoryCountMap[cat] ?? 0) + 1;

            if (!categoryColorMap[cat]) {
                categoryColorMap[cat] = findCategoryOption(categories, cat)?.color ?? place.get("color") ?? "#015EFF";
            }
        });

        const labels = Object.keys(categoryCountMap);
        const values = labels.map((l) => categoryCountMap[l]);
        const colors = labels.map((l) => categoryColorMap[l] ?? "#015EFF");

        // 2026.01.16 | dslee | 웹뷰로 전송할 차트 데이터
        const chartData = {
            labels,
            values,
            colors,
            categoryCountMap,
            totalPlaces: visibleFeatures.length
        };
        
        // 2026.01.16 | dslee | 차트 선택 시 웹뷰 데이터 전송
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'CHART_ANALYSIS_DATA',
                payload: chartData
            }));
        }

        return { labels, values, colors };
    };

    const onMoveRegionHandler = (layerName: LayerName) => {
        return (e: MapEvent) => {
            const map = e.map;

            // 2026.01.16 | dslee | 모바일 & 차트인 경우에 줌 아웃 시 차트 숨김
            if (props.isMobileView && MapToolType.Chart) {
                // 해당 레이어(CHART)에 연결된 모든 Select 인터랙션을 가져와서 비움
                const interactions = getInteractions(map, layerName);
                interactions.forEach((it) => {
                    if (it instanceof Select) {
                        it.getFeatures().clear(); // 여기서 remove 이벤트가 트리거됨
                    }
                });
                setIsOnGraph(false); // 차트 오버레이 숨기기
            }

            const view = map.getView();
            const zoom = view.getZoom();
            const select = getInteractions(map, layerName).find((i) => i instanceof Select) as Select;
            const selectedRegion = select?.getFeatures().getArray()[0];

            const tempSize = map.getSize()!;
            const tempExtent = map.getView().calculateExtent(tempSize);
            // console.log("sizeX\tSizeY\tminx\tminy\tmaxx\tmaxy\tzoom\tresolution");
            // console.log(tempSize[0] + "\t" + tempSize[1] + "\t" + tempExtent[0] + "\t" + tempExtent[1] + "\t" + tempExtent[2] + "\t" + tempExtent[3] + "\t" + zoom + "\t" + view.getResolution());

            if (!select || zoom === undefined) return;

            const layerGroup = getLayerGroup(map, layerName);

            if (!layerGroup) return;

            const visibleLayer = layerGroup.getLayersArray().find((l) => l.getVisible()) as VectorLayer<VectorSource>;

            if (!visibleLayer) return;

            const maxResolution = visibleLayer.get("max");
            const maxZoom = visibleLayer.get("maxZoom");

            // === 줌아웃 로직 (상위 행정구역 전환) ===
            // if (maxResolution !== undefined) {
            //     const maxZoomLv = view.getZoomForResolution(maxResolution);
            //     // const maxZoomLv = view.getZoomForResolution(maxResolution);
            //     // console.log("[ZOOM OUT] maxResolution: ", maxResolution, " / maxZoomLv: ", maxZoomLv, " / zoom: ", zoom, " / reolsution: ", view.getResolution());
            //     if (zoom <= (maxZoomLv ?? 0) - 0.5) {
            //         zoomOutOfSelectedRegion(select, visibleLayer);
            //         return;
            //     }
            // }

            const resolution = map.getView().getResolution();
            const { max } = visibleLayer.getProperties();
            let _max = max;

            // 선택된 지역에 따라 줌아웃 범위 변경 | smyun | 250723
            // 줌아웃 시 selectedRegion undefined
            if (selectedRegion) {
                const recentLayer = select.getLayer(selectedRegion);
                const recentRegionType = recentLayer.get("name") as string;
                const recentRCode = selectedRegion.get(`${recentRegionType}_CD`);
                // console.log(`region 확인 ${recentRCode}`);

                recentRegionCode.current = recentRCode && recentRCode;
            }

            const cd = recentRegionCode.current;
            if (cd !== 0) {
                // console.log(`region 확인 ${cd}`);

                // 크기가 작은 지역 (서울 11, 세종 41, 대전 30, 광주 29, 울산 31, 부산 26)
                if (
                    cd.toString().startsWith("11") ||
                    cd.toString().startsWith("41") ||
                    cd.toString().startsWith("30") ||
                    cd.toString().startsWith("29") ||
                    cd.toString().startsWith("31") ||
                    cd.toString().startsWith("26")
                ) {
                    _max = (max * 1) / 3;
                }
            }
            // ---------

            // 줌아웃 범위 여유 주기
            if (max && resolution! > _max + 50) {
                // if (max && resolution! > max) {
                // console.log(`범위 확인 ${max}`);
                zoomOutOfSelectedRegion(select, visibleLayer);
                return;
            }

            const isChartLayer = layerGroup.get("name") === LayerName.CHART;

            // @@ magicZoomLv 주석 확인 필 @@
            // @@ Need to Check @@
            if (isChartLayer && zoom <= magicZoomLv) {
                zoomOutOfSelectedRegion(select, visibleLayer);
                return;
            }

            const minResolution = visibleLayer.get("min");

            // if (minResolution && resolution! > minResolution) {
            //     // 현재 해상도가 설정된 최소보다 크다 = 더 멀리 있는 상태이므로 줌아웃 필요
            //     zoomOutOfSelectedRegion(select, visibleLayer);
            //     return;
            // }

            // === 줌인 로직 (하위 행정구역 전환) ===
            if (selectedRegion && minResolution !== undefined) {
                // console.log("[ZoomIn]");
                const minZoomLv = view.getZoomForResolution(minResolution);
                if (zoom >= (minZoomLv ?? 0) + 0.3) {
                    zoomIntoSelectedRegion(select, selectedRegion);
                    return;
                }
            }
        };
    };

    /**
     * 접근성분석
     */
    const onAddFeature = useCallback(
        (e: VectorSourceEvent<Geometry>, map: OlMap, draw: Draw) => {
            // point 개수 제한
            const source = e.target as VectorSource;
            const features = source?.getFeatures();
            if (!features) return;

            setAccState(features);

            if (features.length === accessibilityCondition.MAX_POINTS) {
                map.removeInteraction(draw);
            }
        },
        [mapObj]
    );

    const onRemoveFeature = useCallback(
        (e: VectorSourceEvent<Geometry>, map: OlMap, interactions: Interaction[]) => {
            const source = e.target as VectorSource;
            const features = source?.getFeatures();
            if (!features) return;

            setAccState(features);

            if (features.length === accessibilityCondition.MAX_POINTS - 1) {
                removeInteractions(map, LayerName.OVERLAP);
                interactions.forEach((i) => map.addInteraction(i)); // draw, modify, select 순서대로 들어가야 select가 제대로 동작함
            }
        },
        [mapObj]
    );

    const attachRemoveHandlerToLastPoint = (map: OlMap, source: VectorSource, overlay: Overlay, overlayElem: HTMLDivElement) => {
        const remaining = source.getFeatures();
        if (remaining.length === 0) return;

        const last = remaining.reduce((latest, f) => (f.get("addedAt") > latest.get("addedAt") ? f : latest));

        const coord = (last.getGeometry() as Point).getCoordinates();

        overlayElem.onclick = () => {
            source.removeFeature(last);
            map.removeOverlay(overlay);
            // 삭제 후 다시 붙이기
            attachRemoveHandlerToLastPoint(map, source, overlay, overlayElem);
        };

        overlay.setElement(overlayElem);
        overlay.setPosition(coord);
        map.addOverlay(overlay);
    };

    const onSelectFeature = useCallback(
        (e: CollectionEvent<OlFeature<Geometry>>, map: OlMap, source: VectorSource, overlay: Overlay) => {
            map.removeOverlay(overlay);

            const overlayElem = removeRef.current;
            const feature = e.element;
            const point = feature.getGeometry() as Point;
            const coordinate = point.getCoordinates();

            if (!feature.get("addedAt")) {
                feature.setProperties({ addedAt: Date.now() });
            }

            if (!overlayElem) return;

            overlayElem.classList.add(styles.show);

            overlayElem.onclick = () => {
                source.removeFeature(feature);
                map.removeOverlay(overlay);
                attachRemoveHandlerToLastPoint(map, source, overlay, overlayElem);
            };

            overlay.setElement(overlayElem);
            overlay.setPosition(coordinate);
            map.addOverlay(overlay);
        },
        [mapObj, removeRef]
    );

    // selectedMapIdx와 overlapMapIDs를 ref로 관리하여 최신 값 보장
    const selectedMapIdxRef = useRef(props.selectedMapIdx);
    selectedMapIdxRef.current = props.selectedMapIdx;

    const overlapMapIDsRef = useRef(overlapMapIDs);
    overlapMapIDsRef.current = overlapMapIDs;

    const getCurrentMapId = useCallback(() => {
        const currentMapIdx = selectedMapIdxRef.current;
        const currentOverlapMapIDs = overlapMapIDsRef.current;

        if (currentMapIdx === 0 || currentMapIdx === undefined) {
            return baseMapID;
        } else if (currentOverlapMapIDs && currentMapIdx > 0) {
            return currentOverlapMapIDs[currentMapIdx - 1]; // 인덱스 보정
        }
        return null; // 명시적 반환값
    }, [baseMapID]);

    // !@#!-M1-OSC1. 오버레이 클러스터/마커 선택
    const onSelectCluster = useCallback(
        (e: CollectionEvent<OlFeature>, layer: Layer, layerMapid: string) => {
            const cluster = e.element as OlFeature;
            const features = cluster.get("features");
            let size = features?.length;

            // 실제 클릭된 마커의 mapid를 피처 ID에서 추출
            let actualMapid = layerMapid; // 기본값

            if (features && features.length > 0) {
                const firstFeature = features[0] as OlFeature;
                const featureId = firstFeature.getId() as string;
                if (featureId && featureId.includes(".")) {
                    actualMapid = featureId.split(".")[0];
                }
            }

            // 마커/클러스터 클릭해서 오버랩 전환하는 분기 처리
            // 기존 로직: 활성화된 지도의 마커 선택 (disableSelect 조건 제거로 모든 마커 선택 가능)
            if (size === 1) {
                const feature = features[0] as OlFeature;
                const id = feature.getId() as string;

                const featureMapId = id.split(".")[0];
                const currentMapId = getCurrentMapId();

                if (featureMapId !== currentMapId) {
                    const targetFeatureXID = id.split(".")[1] ? Number(id.split(".")[1]) : Number(id);

                    // 25.09.12 | Kim Young Jun | 지도 전환 후 하이라이트 복원을 위한 하이라이트 피처 정보 저장
                    pendingHighlightRef.current = {
                        mapId: featureMapId,
                        xid: targetFeatureXID,
                    };

                    // 클릭된 마커가 속한 지도의 인덱스 찾기
                    let targetMapIdx = 0;

                    if (featureMapId === baseMapID) {
                        targetMapIdx = 0; // 기본 지도
                    } else if (overlapMapIDsRef.current) {
                        const overlapIndex = overlapMapIDsRef.current.findIndex((id) => id === featureMapId);
                        if (overlapIndex !== -1) {
                            targetMapIdx = overlapIndex + 1; // 오버랩 지도 (1부터 시작)
                        }
                    }

                    // selectedMapIdx 변경 (useEffect에서 자동으로 switchOverlap 호출됨)
                    if (props.setSelectedMapIdx) {
                        props.setSelectedMapIdx(targetMapIdx);
                    }
                    return;
                }

                // 현재 지도의 마커 선택 - 정상 로직 실행
                // 25.09.12 | Kim Young Jun | 마커 직접 클릭 상태 (애니메이션 비활성화용)
                // isDirectMarkerClickRef.current = true;

                // highlightManager.clearAllSelections();
                feature.set("selected", true); // 선택 상태 표시(스타일 함수가 참고하면 필요)
                highlightManager.selectSingleMarker(feature); // 새 마커 하이라이트 적용(내부에서 상태 트래킹)

                // 마커 ID에서 XID 추출 - format: "mapid.xid"
                const xid = id.split(".")[1] ? Number(id.split(".")[1]) : Number(id);
                props.setSelectedPoiIdx(xid);

                props.setShowFloatingSheet && props.setShowFloatingSheet(true); // 시트 열기
            } else if (size > 1) {
                highlightManager.selectCluster(cluster, features);

                const currentMapId = getCurrentMapId();

                const isAllFromCurrentMap = features.every((feature: OlFeature) => {
                    const id = feature.getId() as string;
                    const featureMapId = id.split(".")[0];
                    return featureMapId === currentMapId;
                });

                if (!isAllFromCurrentMap) {
                    // 클러스터에서 첫 번째 마커의 지도로 전환
                    const firstFeature = features[0] as OlFeature;
                    const firstFeatureId = firstFeature.getId() as string;
                    const firstFeatureMapId = firstFeatureId.split(".")[0];

                    // 첫 번째 마커가 속한 지도의 인덱스 찾기
                    let targetMapIdx = 0;

                    if (firstFeatureMapId === baseMapID) {
                        targetMapIdx = 0; // 기본 지도
                    } else if (overlapMapIDsRef.current) {
                        const overlapIndex = overlapMapIDsRef.current.findIndex((id) => id === firstFeatureMapId);
                        if (overlapIndex !== -1) {
                            targetMapIdx = overlapIndex + 1; // 오버랩 지도 (1부터 시작)
                        }
                    }

                    // selectedMapIdx 변경 (useEffect에서 자동으로 switchOverlap 호출됨)
                    if (props.setSelectedMapIdx) {
                        props.setSelectedMapIdx(targetMapIdx);
                    }

                    // 1000ms 후 지도 전환만 완료 (XID 설정 없이)
                    setTimeout(() => {
                        // XID 설정 없이 전환만 완료
                    }, 500);

                    return;
                }
            }
        },
        [
            mapObj,
            props.setSelectedPoiIdx,
            props.setSelectedMapIdx,
            props.mapInfos,
            props.selectedMapIdx,
            props.setShowFloatingSheet,
            highlightManager,
            baseMapID,
            getCurrentMapId,
        ]
    );

    // 25.07.10 | Kim Young Jun | 행정구역 분석 데이터 업데이트
    const updateAnalysisWithCurrentRegion = useCallback(() => {
        if (!mapObj || !baseMapID) return;

        const name = LayerName.CHOROPLETH;
        const layerGroup = getLayerGroup(mapObj, name);

        if (!layerGroup) return;

        // 현재 표시 중인 행정구역 레이어 찾기
        const visibleLayer = layerGroup.getLayersArray().find((l) => l.getVisible()) as VectorLayer<VectorSource>;

        if (!visibleLayer) {
            // 표시 중인 레이어가 없으면 기본적으로 전국 단위 데이터 로드
            reqAnalysisData(mapObj, baseMapID, "0", name, true, layerGroup, analysisMetricRef.current!);
            return;
        }

        // 표시 중인 레이어 있으면 해당 행정구역에 대한 데이터 로드
        reqAnalysisData(mapObj, baseMapID, props.currentRegionCode!, name, false, visibleLayer, analysisMetricRef.current!);
    }, [mapObj, baseMapID, props.currentRegionCode, analysisMetricRef.current]);

    /**
     * 분석도구 > 범위분석
     */

    const highLightExtent = (e: DrawEvent, type?: number) => {
        // type => 0: 다각형, 1: 원형
        const map = e.target.getMap();
        const source = getLayer(map, LayerName.ExtentAnalysis)?.getSource() as VectorSource;

        if (!source) return;

        const extent = map.getView().get("extent");
        const polygon = new OlPolygon([
            [
                [extent[0], extent[1]],
                [extent[2], extent[1]],
                [extent[2], extent[3]],
                [extent[0], extent[3]],
                [extent[0], extent[1]],
            ],
        ]);

        const overlayFeature = new OlFeature(polygon);

        // 다각형
        if (type == 25) {
            // console.log("[DEBUG] extentAnalysis: 다각형 그리기");
            // overlayFeature.setStyle(extentAnalysisStyle); // 그리는 시점 스타일

            const drawFeature: OlFeature<OlPolygon> = e.feature as OlFeature<OlPolygon>;
            const drawFeatureLinearRing = drawFeature.getGeometry()?.getLinearRings();

            drawFeatureLinearRing?.forEach((LR) => {
                overlayFeature.getGeometry()?.appendLinearRing(LR);
                const coordinates = LR.getCoordinates();
                coordinates.forEach((coordinate) => {
                    const vertexFeature = new OlFeature({
                        geometry: new Point(coordinate),
                    });
                    // vertexFeature.setStyle(
                    //     new Style({
                    //         image: blueCircleStyle,
                    //     })
                    // );
                    source.addFeature(vertexFeature);
                });
            });

            source.addFeature(overlayFeature as OlFeature);
            // drawFeature.setStyle(extentAnalysisPolyStyle); // 완성된 폴리곤 스타일
        } else {
            // 원형
            const feature = e.feature as OlFeature;
            const line = feature.getGeometry() as LineString;
            const coords = line.getCoordinates();

            if (coords.length !== 2) return;

            const center = line.getFirstCoordinate(); // 원 중심
            const center_ = fromLonLat(center, "EPSG:5179");
            const radius = line.getLength();

            const circle = new Circle(center_, radius);

            // 원을 다각형으로 변환 (기본적으로 원을 근사하는 다각형 생성)
            const coordinates = circle.getCoordinates(); // 원의 좌표 얻기
            const numPoints = 64; // 원을 근사할 때 사용할 점의 수 (각도를 나누는 개수)

            const polygonCoords = [];
            for (let i = 0; i < numPoints; i++) {
                const angle = (i / numPoints) * 2 * Math.PI; // 각도 계산
                const x = center[0] + radius * Math.cos(angle); // x 좌표
                const y = center[1] + radius * Math.sin(angle); // y 좌표
                polygonCoords.push([x, y]);
            }

            // 폴리곤 생성
            const polygon_circle = new OlPolygon([polygonCoords]);
            const drawFeature = new OlFeature(polygon_circle);
            const drawFeatureLinearRing = drawFeature.getGeometry()?.getLinearRings();

            drawFeatureLinearRing?.forEach((LR) => {
                overlayFeature.getGeometry()?.appendLinearRing(LR);
            });

            source.addFeature(overlayFeature as OlFeature);
            drawFeature.setStyle(extentAnalysisCircleStyle); // 완성된 폴리곤 스타일
        }

        // 외부 벡터레이어 생성(검정배경)
        const outSideVectorSource = new VectorSource({
            features: [new OlFeature(polygon)],
        });

        const outSideVectorLayer = new VectorLayer({
            source: outSideVectorSource,
            style: extentAnalysisOutsideStyle,
        });

        map.addLayer(outSideVectorLayer);

        return polygon;
    };

    // olmap 관련 ----------------------------------------------------------------
    // 25.09.12 | Kim Young Jun | 펼쳐진 클러스터의 스타일은 레이어와 별도로 관리되며, 이 스타일을 동적으로 관리하기 위한 로직의 복잡성이 높음
    // "펼쳐진 클러스터 내 마커 하이라이트 해제가 동작하지 않는 이슈" / "오버랩 지도 전환 시 펼쳐진 클러스터가 남아있는 이슈" 해결을 위해 클러스터를 닫기 위해 줌 레벨 변경하여 처리...
    const adjustZoom = (map: OlMap, duration = 400) => {
        const view = map.getView();
        const [max, min, current] = [
            view.getZoomForResolution(view.getMinResolution()),
            view.getZoomForResolution(view.getMaxResolution()),
            view.getZoom(),
        ];
        if (!max || min === undefined || current === undefined) return;

        if (zoom > max) {
            setZoom(max);
            return;
        } else if (zoom < min) {
            setZoom(min);
            return;
        }

        // 25.09.12 | Kim Young Jun | FloatingSideSheet 닫기로 인한 줌 변경인 경우 duration을 0으로 설정
        if (isClosingFloatingSideSheetRef.current) {
            duration = 0;
            isClosingFloatingSideSheetRef.current = false; // 플래그 리셋
        }
    };

    const sideSheetContentRef = useRef(props.sideSheetCont);
    useEffect(() => {
        sideSheetContentRef.current = props.sideSheetCont;

        // 수정/추가 모드가 끝나면 마커의 이전 위치 추적을 종료한다.
        if (props.sideSheetCont !== ContentList.AddSinglePlace && props.sideSheetCont !== ContentList.EditPlace) {
            revertPositionRef.current = null;
            translateGeometry.current = null;
        }
    }, [props.sideSheetCont]);

    const disallowedContents = [ContentList.EditPlace, ContentList.AddSinglePlace, ContentList.AddPlace];

    // 마커 선택 모드 조건
    const customClickCondition = (event: any) => {
        const currentContent = sideSheetContentRef.current;
        if (currentContent === undefined) return false;

        const shouldAllowClick = !disallowedContents.includes(currentContent); // 수정모드, 신규장소등록 모드 가 아닐 경우에만 클릭 허용
        return shouldAllowClick && singleClick(event);
    };

    const setClusterInteractions = (map: OlMap, layer: Layer) => {
        const { name, mapid } = layer.getProperties();

        const select = new SelectCluster({
            pointRadius: 30,
            maxObjects: 60,
            layers: [layer],
            condition: customClickCondition,
            featureStyle: revealedStyleFunction,
            style: selectedClusterStyleFunction,
            spiral: true,
        });

        const hover = new (Hover as any)({
            layerFilter: (l: Layer) => (l.get("name") === name && l.get("mapid") === mapid) || l.get("name") === LayerName.CLUSTER_OVERLAY,
            featureFilter: (f: OlFeature) => f.getGeometry()?.getType() === "Point",
            handleEvent: () => true,
        });

        // **중요: 이벤트 리스너 중복 방지**
        // select.getFeatures().clear(); // 기존 선택 상태 초기화

        // 이벤트 리스너를 한 번만 바인딩
        select.getFeatures().on("add", (e: CollectionEvent<OlFeature>) => {
            onSelectCluster(e, layer, mapid);
        });

        select.getFeatures().on("remove", (e: CollectionEvent<OlFeature>) => {
            const removedFeature = e.element;
            highlightManager.unselectFeature(removedFeature);
        });

        // 인터랙션에 고유 식별자 설정
        select.setProperties({
            name: `${name}_${mapid}`, // 고유한 이름 설정
            mapid: mapid,
        });

        hover.setProperties({
            name: `${name}_hover_${mapid}`, // 고유한 이름 설정
            mapid: mapid,
        });

        map.addInteraction(select);
        map.addInteraction(hover);

        // 수정상태에서는 마커에 마우스 포인터 스타일 제거
        map.on("pointermove", (event) => {
            const currentContent = sideSheetContentRef.current;
            if (currentContent === undefined) return false;

            const shouldAllowPointer = !disallowedContents.includes(currentContent);

            const features = map.getFeaturesAtPixel(event.pixel);
            const hasPointFeature = features?.some((f) => f.getGeometry()?.getType() === "Point");

            if (shouldAllowPointer && hasPointFeature) {
                map.getTargetElement().style.cursor = "pointer";
            } else {
                map.getTargetElement().style.cursor = "";
            }
        });
    };

    const setMapPadding = (map: OlMap, padding: number) => {
        const view = map.getView();

        // 현재 애니메이션 상태 확인
        if (view.getAnimating()) {
            // 애니메이션 완료를 기다림
            const checkAnimation = () => {
                if (!view.getAnimating()) {
                    view.padding = [0, 0, 0, padding];
                } else {
                    requestAnimationFrame(checkAnimation);
                }
            };
            requestAnimationFrame(checkAnimation);
        } else {
            view.padding = [0, 0, 0, padding];
        }
    };

    /**
     * 맵에서 등록된 Interaction 중 SelectCluster 인스턴스를 찾아 반환한다.
     * SelectCluster는 ol-ext에서 제공하는 클러스터 선택용 Interaction이며,
     * 클러스터 마커를 클릭하거나 프로그래밍적으로 조작할 때 사용.
     *
     * @param map - OpenLayers 맵 객체
     * @param targetLayer - 특정 레이어에 대한 interaction을 찾을 경우
     * @returns 맵에 등록된 SelectCluster 인스턴스 또는 없을 경우 undefined
     */
    const getSelectClusterInteraction = (map: OlMap, targetLayer?: Layer): SelectCluster | undefined => {
        // SelectCluster 타입의 인스턴스들을 모두 찾음
        const interactions = map
            .getInteractions()
            .getArray()
            .filter((i) => i instanceof SelectCluster) as SelectCluster[];

        if (interactions.length === 0) {
            return undefined;
        }

        // 특정 레이어가 지정된 경우, 해당 레이어를 대상으로 하는 interaction 찾기
        if (targetLayer) {
            const targetInteraction = interactions.find((interaction) => {
                const layers = interaction.get("layers") || [];
                return layers.includes(targetLayer);
            });

            if (targetInteraction) return targetInteraction;
        }

        // 기본적으로 첫 번째 interaction 반환 (기본 지도용)
        return interactions[0];
    };

    /**
     * 파라메터로 받은 feature가 포함된 클러스터 feature를 반환한다.
     *
     * @param map - OpenLayers 지도 객체
     * @param feature - 포함 여부를 확인할 대상 feature
     * @returns feature를 포함하는 클러스터 feature 또는 undefined
     */
    const getClusteredFeaturesByFeature = (map: OlMap, feature: OlFeature): OlFeature | undefined => {
        let layer: ClusterLayer | null = null;
        let source: ClusterSource | null = null;

        // 피처의 ID에서 mapid 추출
        const featureId = feature.getId() as string;
        const featureMapId = featureId ? featureId.split(".")[0] : null;

        // 현재 선택된 지도가 오버랩 지도인지 확인
        if (props.selectedMapIdx! > 0 && featureMapId) {
            // 오버랩 지도인 경우: 해당 mapid와 일치하는 오버랩 레이어에서 찾기
            const overlapLayerGroup = getLayerGroup(map, LayerName.OVERLAP);

            if (overlapLayerGroup) {
                const overlapLayers = overlapLayerGroup.getLayersArray() as ClusterLayer[];
                layer = overlapLayers.find((l) => l.get("mapid") === featureMapId) || null;
                source = (layer?.get("source") as ClusterSource) || null;
            }
        } else {
            // 기본 지도인 경우: 기본 클러스터 레이어 사용
            layer = getLayer(map, LayerName.CLUSTER) as ClusterLayer;
            source = layer?.get("source") as ClusterSource;
        }

        if (!layer || !source) return;

        return source.getFeatures().find((clusterFeature) => {
            const features = clusterFeature.get("features") as Feature[] | undefined;
            return features?.some((f) => f instanceof OlFeature && f.getId() === feature.getId());
        });
    };

    /**
     * 주어진 feature가 포함된 클러스터를 펼치고, 해당 feature를 선택 상태로 표시한다.
     *
     * @param map - OpenLayers 지도 객체
     * @param feature - 선택할 대상 feature
     */
    const openClusterByFeature = (map: OlMap, feature: OlFeature) => {
        // 해당 feature가 포함된 클러스터 feature를 찾아온다.
        const containingCluster = getClusteredFeaturesByFeature(map, feature);

        if (!containingCluster) return;

        // 피처의 ID에서 mapid 추출하여 해당하는 레이어 찾기
        const featureId = feature.getId() as string;
        const featureMapId = featureId ? featureId.split(".")[0] : null;

        let targetLayer: Layer | undefined = undefined;

        // 현재 선택된 지도가 오버랩 지도인지 확인
        if (props.selectedMapIdx! > 0 && featureMapId) {
            // 오버랩 지도인 경우: 해당 mapid와 일치하는 오버랩 레이어 찾기
            const overlapLayerGroup = getLayerGroup(map, LayerName.OVERLAP);

            if (overlapLayerGroup) {
                const overlapLayers = overlapLayerGroup.getLayersArray() as ClusterLayer[];
                targetLayer = overlapLayers.find((l) => l.get("mapid") === featureMapId);
            }
        } else {
            // 기본 지도인 경우: 기본 클러스터 레이어 사용
            targetLayer = getLayer(map, LayerName.CLUSTER);
        }

        // 해당 레이어에 대한 SelectCluster interaction을 가져온다.
        const scInteraction = getSelectClusterInteraction(map, targetLayer);

        // 클러스터가 존재하고 SelectCluster interaction이 있는 경우에만 실행
        if (scInteraction) {
            scInteraction.getFeatures().clear();
            // 클러스터를 펼친다 (흰 배경)
            scInteraction.selectCluster(containingCluster);

            // SelectCluster interaction의 feature 컬렉션을 가져온다.
            const scInteractionFeatures = scInteraction.getFeatures();

            // interaction에 클러스터 feature를 추가하여 마커들이 지도에 보이도록 한다
            scInteractionFeatures.push(containingCluster);

            // 선택된 feature에 'selected' 속성을 true로 설정하여 하이라이팅 스타일이 적용되도록 한다.
            feature.set("selected", true);
            // feature.setProperties({ selected: true });
        }
    };

    const selectFeatureOnList = (map: OlMap, idx: number, mapid: string /*enableAnimation: boolean = true*/) => {
        console.log(`[selectFeatureOnList] 시작: idx=${idx}, mapid=${mapid}`);

        // 애니메이션 진행 상태 체크
        const view = map.getView();
        if (view.getAnimating()) {
            console.log("[selectFeatureOnList] 애니메이션 진행 중이므로 대기");
            // 애니메이션 완료 후 재시도
            setTimeout(() => {
                selectFeatureOnList(map, idx, mapid);
            }, 100);
            return;
        }

        const targetId = `${mapid}.${idx}`;
        console.log(`[selectFeatureOnList] 타겟 ID: ${targetId}`);

        // 오버랩 지도에서 기준 지도로 전환이 필요한지 확인
        const isOverlapToBase = props.selectedMapIdx! > 0 && mapid === baseMapID;

        if (isOverlapToBase) {
            // 기준 지도로 전환하고 selectedMapIdx를 0으로 변경
            // switchOverlap(map); // 기준 지도로 전환
            if (props.setSelectedMapIdx) {
                props.setSelectedMapIdx(0);
            }

            // 오버랩에서 기준 지도로 전환 시 FloatingSideSheet가 확실히 열리도록 보장
            if (props.setShowFloatingSheet) {
                // 다음 렌더링 사이클에서 실행하여 selectedMapIdx 변경 이후에 처리
                setTimeout(() => {
                    console.log("[selectFeatureOnList] 오버랩->기준 지도 전환: FloatingSideSheet 강제 열기");
                    props.setShowFloatingSheet!(true);
                }, 0);
            }
        }

        let layer: ClusterLayer | null = null;
        let source: ClusterSource | null = null;
        let vectorSource: VectorSource | null = null;

        // 현재 선택된 지도가 오버랩 지도인지 확인
        if (props.selectedMapIdx! > 0 && !isOverlapToBase) {
            // 오버랩 지도인 경우: 오버랩 레이어에서 해당 mapid와 일치하는 레이어 찾기
            const overlapLayerGroup = getLayerGroup(map, LayerName.OVERLAP);

            if (overlapLayerGroup) {
                const overlapLayers = overlapLayerGroup.getLayersArray() as ClusterLayer[];
                layer = overlapLayers.find((l) => l.get("mapid") === mapid) || null;
                source = (layer?.get("source") as ClusterSource) || null;
                vectorSource = source?.getSource() || null;
            }
        } else {
            // 기본 지도인 경우: 기본 클러스터 레이어 사용
            layer = getLayer(map, LayerName.CLUSTER) as ClusterLayer;
            source = layer?.get("source") as ClusterSource;
            vectorSource = source?.getSource() || null;
        }

        if (!layer || !source || !vectorSource) return;

        const feature = vectorSource.getFeatureById(`${mapid}.${idx}`);

        if (!feature) return;

        // 1. FloatingSideSheet 열기 (상태 변경 즉시 처리)
        console.log("[selectFeatureOnList] 마커 선택 완료, 시트 열기 상태 setShowFloatingSheet:", props.setShowFloatingSheet);
        console.log("[selectFeatureOnList] 마커 선택 완료, 시트 열기 상태 showFloatingSheet:", props.showFloatingSheet);

        // 오버랩->기준 지도 전환이 아닌 경우에만 즉시 FloatingSideSheet 열기
        if (props.setShowFloatingSheet && !props.showFloatingSheet && !isOverlapToBase) {
            console.log("[selectFeatureOnList] FloatingSideSheet 열기");
            props.setShowFloatingSheet(true);
        }

        if (feature?.get("selected")) return;

        const coordinates = (feature.getGeometry() as Point).getCoordinates();

        const currentView = map.getView();
        const currentZoom = currentView.getZoom() || 0;

        // 타겟 줌 레벨 계산 (최소 13레벨, 현재가 13 이상이면 현재+1)
        const targetZoom = currentZoom < 13 ? 13 : Math.min(currentZoom + 1, 15);

        // 25.09.08 | Kim Young Jun | 안정적인 POI 선택 처리
        // 2. 먼저 마커 선택 (즉시 처리)
        // highlightManager.clearAllSelections();
        highlightManager.selectSingleMarker(feature);

        // 3. 짧은 지연 후 애니메이션 시작 (렌더링 완료 후)
        requestAnimationFrame(() => {
            currentView.animate({
                center: coordinates,
                zoom: targetZoom,
                duration: 600,
            });

            // 4. 애니메이션 완료 후 클러스터 펼치기
            setTimeout(() => {
                if (feature && !props.isNotUseMarkerAnalysis) {
                    openClusterByFeature(map, feature);
                }
            }, 500);
        });

        // 25.09.12 | 애니메이션 활성화 여부에 따라 조건부 실행
        // if (enableAnimation) {
        // }
    };

    const replaceHeatMapLayer = (map: OlMap) => {
        const layer = getLayer(map, LayerName.HEAT) as HeatmapLayer;
        const layerGroup = map.getLayerGroup();
        layerGroup.getLayers().remove(layer);
        
        const clusterLayer = getLayer(map, LayerName.CLUSTER) as ClusterLayer;
        const clusterSource = clusterLayer.get("source") as ClusterSource;
        const vectorSource = clusterSource.getSource() as VectorSource;

        const heatMapLayer = new VariableRadiusHeatmap({
                    source: vectorSource,
                    visible: false,
                    blur: 10,
                    opacity: 0.8, // 투명도
                    weight: (f: OlFeature<Point>) => f.get("weight") ?? 0.5,
                    properties: {
                        name: LayerName.HEAT, // 히트맵
                        zIndex: 2,
                    },
                });

        layerGroup.getLayers().push(heatMapLayer);
    }



    // TODO mjkang 신규 추가 미리보기 레이어에서 피처를 선택할 때
    const selectNewFeature = (map: OlMap, idx: string) => {};

    /**
     * 장소 불러오기(지도 표현)
     * @param data
     */

    const setFeatures = (map: OlMap, data: FeatureCollection, mapid: string, isAnalysis?: boolean, doFit?: boolean) => {
        // const featureLayer = getLayer(map, LayerName.FEATURE);
        let clusterLayer = getLayer(map, LayerName.CLUSTER) as ClusterLayer;

        const features = new GeoJSON().readFeatures(data, { dataProjection: "EPSG:5179", featureProjection: map.getView().getProjection() });

        // const vectorSource = featureLayer?.getSource() as VectorSource;

        // if (!featureLayer || !clusterLayer) return;
        if (!clusterLayer) return;

        // 25.09.08 | Kim Young Jun | 현재 선택된 피처 ID 저장 (소스 클리어 전에)
        const currentSelectedId = highlightManager?.getSelectedMarkerId();

        // 기존 클러스터 레이어 완전 제거 및 새로 생성
        const layerGroup = map.getLayerGroup();
        layerGroup.getLayers().remove(clusterLayer);

        // 새로운 클러스터 레이어 생성
        const newClusterLayer = createClusterLayer(LayerName.CLUSTER) as ClusterLayer;
        const source = newClusterLayer.get("source") as ClusterSource;
        const newVectorSource = source.getSource() as VectorSource;

        // 새 클러스터 레이어를 지도에 추가
        layerGroup.getLayers().push(newClusterLayer);
        clusterLayer = newClusterLayer;

        features.forEach((feature) => {
            // console.log("    @@ feature: ", feature);
            // 항상 기존 맵의 카테고리(option) 기반 스타일로 덮어씀
            const categoryName = feature.get("category");
            const xid = (feature.getId() as string).split(".")[1];
            const option = findCategoryOption(props.categories, categoryName);
            const famous = props.famousMarkers?.filter((item) => item.XID === Number(xid))[0];

            // 251125 | smyun | 'category 선택안함' 지도기능 추가. metadata에 넣어준 makerstyle 이용(options 값 없음)
            feature.setProperties({
                icon: option?.icon ?? metaData?.metadata.marker ?? "marker",
                color: option?.color ?? metaData?.metadata.markerColor ?? Color.BASIC_MARKER,
                famousImg: famous ? famous.url : undefined,
            });
        });

        newVectorSource && newVectorSource.clear();

        // 기존 클러스터 소스에 새로운 피처들 추가
        if (newVectorSource.getFeatures().length === 0) {
            // 25.09.08 | Kim Young Jun | 기준지도에는 기준지도 ID(baseMapID) 설정, 오버랩지도가 아닌 기준지도용
            clusterLayer.setProperties({ mapid: baseMapID });
            clusterLayer.setStyle(clusterStyleFunction);

            setClusterInteractions(map, clusterLayer);
        }

        // gsshin: "selectedIdxRef.current === 0" 조건 추가
        // 보고있는 아이템이 있는경우 전체영역으로 Fit 안되도록 한다.
        // 25.07.21 | Kim Young Jun | fit 실행 조건 개선
        const shouldFit =
            doFit !== false && // doFit가 명시적으로 false가 아니고
            !props.accState?.vehicle && // 접근성 분석 중이 아니고
            !isAnalysis && // 분석 모드가 아니고
            !highlightManager?.getSelectedMarkerId(); // 선택된 마커가 없을 때만

        if (shouldFit) {
            newVectorSource.once("change", (e: any) => {
                const extent = (e.target as VectorSource).getExtent();
                // 26.01.12 | dslee | 빈 extent 체크, 피처가 0개일 때 Infinity 반환되므로, 이 경우 실행하지 않음
                if (extent && extent.every((val) => isFinite(val))) {
                    fitToExtent(map, extent);
                }
            });
        }

        newVectorSource.addFeatures(features);

        // 줌 레벨에 따라 표시 레이어 변경 ============================================
        const toggleLayersByZoom = () => {
            // console.log("@@@@@ toggleLayersByZoom @@@@@");

            const zoom = map.getView().getZoom() ?? 0;
            const currentClusterSource = clusterLayer.get("source") as ClusterSource; // 현재 클러스터 소스 참조

            // featureLayer.setVisible(false); // 항상 포인트 레이어는 숨김
            clusterLayer.setVisible(true); // 항상 클러스터 레이어는 표시

            if (zoom <= 5) {
                currentClusterSource.setDistance(200); // 대한민국 전체 범위 보기
            } else if (zoom <= 11) {
                currentClusterSource.setDistance(150); // 도/시 단위
            } else {
                currentClusterSource.setDistance(100); // 구/동 단위
                // featureLayer.setVisible(true); // 25.07.07 | Kim Young Jun | 포인트 레이어 제거
            }

            // 25.07.02 | Kim Young Jun | 현재 선택된 피처 메모
            const currentSelectedIdx = selectedPoiIdxRef.current;

            // 25.07.02 | Kim Young Jun | 선택된 feature 하이라이트 유지
            // 25.08.27 | Kim Young Jun | 편집 상태 체크 및 위치 복원 추가
            if (currentSelectedIdx) {
                const feature = newVectorSource.getFeatureById(`${mapid}.${currentSelectedIdx}`);
                feature && highlightManager.selectSingleMarker(feature);

                // 편집 중인 마커의 위치 복원
                if (feature && highlightManager.isEditingFeature(`${mapid}.${currentSelectedIdx}`)) {
                    const tempCoords = highlightManager.getTemporaryCoordinates(`${mapid}.${currentSelectedIdx}`);
                    if (tempCoords) {
                        const geometry = feature.getGeometry();
                        if (geometry instanceof Point) {
                            geometry.setCoordinates(tempCoords);
                        }
                    }
                }
            }

            // 25.09.12 | Kim Young Jun | 지도 전환 중 대기 중인 하이라이트 복원
            if (pendingHighlightRef.current && pendingHighlightRef.current.mapId === mapid) {
                const pendingXid = pendingHighlightRef.current.xid;
                const pendingFeature = newVectorSource.getFeatureById(`${mapid}.${pendingXid}`);

                // console.log("[DEBUG] 지도 전환 후 하이라이트 복원 시도:", `${mapid}.${pendingXid}`, pendingFeature ? "발견" : "미발견");

                if (pendingFeature) {
                    // 하이라이트 적용
                    pendingFeature.set("selected", true);
                    // pendingFeature.setProperties({ selected: true });
                    highlightManager.selectSingleMarker(pendingFeature);

                    // selectedPoiIdx도 업데이트
                    props.setSelectedPoiIdx(pendingXid);

                    // console.log("[DEBUG] 지도 전환 후 하이라이트 복원 완료:", `${mapid}.${pendingXid}`);
                }

                // 복원 완료 후 대기 정보 클리어
                pendingHighlightRef.current = null;
            }
        };

        
        //필터링될때마다 히트맵 재생성
        replaceHeatMapLayer(map);        

        // 초기 체크
        toggleLayersByZoom();

        // 줌 변경 감지(행정경계, 단계구분도, 차트, 버블맵, 히트맵 메뉴가 아닐 경우에만 실행)
        const excludedTypes = [MapToolType.Region, MapToolType.Choropleth, MapToolType.Chart, MapToolType.Bubble, MapToolType.Heat];

        map.getView().on("change:resolution", () => {
            syncing = true;
            if (!excludedTypes.includes(activeIdxRef.current)) {
                toggleLayersByZoom();
            }
            setTimeout(() => {
                syncing = false;
            }, 500); // 500ms 동안 수동 줌 상태 유지
        });

        // 25.07.18 | Kim Young Jun | 분석 상태 초기화
        prevAnalysisStateRef.current = false;

        
        if( activeIdxRef.current === 4){
            setHeatBlurByMetric(map);
        }
    };

    const initialOverlapToastShown = useRef<Set<string>>(new Set());

    /**
     * Overlap 지도의 feature 데이터 설정
     */
    const setOverlapFeatures = (map: OlMap, data: FeatureCollection, mapid: string, mapInfo: MapInfo, metadata: Metadata) => {
        console.log(`@@@@@@@@ [Map] setOverlapFeatures() 시작 - mapid: ${mapid}, 피처 수: ${data.features.length}`);

        const name = LayerName.OVERLAP;
        const features = new GeoJSON().readFeatures(data, { dataProjection: "EPSG:5179", featureProjection: map.getView().getProjection() });
        const overlapLayerGroup = getLayerGroup(map, name);

        if (!overlapLayerGroup) {
            console.log("@@@@@@@@ [Map] setOverlapFeatures() - overlapLayerGroup이 없음");
            return;
        }

        console.log(`@@@@@@@@ [Map] setOverlapFeatures() - 현재 오버랩 레이어 그룹의 레이어 수: ${overlapLayerGroup.getLayersArray().length}`);

        // 카테고리 클릭 후 setOverlapFeature 할 시 기존에 있던 데이터 클리어
        const OverlapLayerArray = overlapLayerGroup.getLayersArray();
        // 주어진 mapid와 일치하는 오버랩 지도 레이어 탐색
        const targetOverlapLayer = OverlapLayerArray.find((layer) => layer.get("mapid") === mapid) as ClusterLayer;

        // 지도가 전환될때 마다 setFeatures/setOverlapFeatures가 호출되므로, targetOverlapLayer 초기화 필요
        if (targetOverlapLayer) {
            console.log(`@@@@@@@@ [Map] setOverlapFeatures() - 기존 레이어 ${mapid} 제거 중`);
            const targetSource = (targetOverlapLayer.get("source") as ClusterSource)?.getSource() as VectorSource;

            if (targetSource) {
                console.log(`     >>> 기존 레이어의 피처 수: ${targetSource.getFeatures().length}`);
                targetSource.clear(); // 기존 feature들 제거
            }

            overlapLayerGroup.getLayers().remove(targetOverlapLayer); // 레이어 그룹에서도 제거
        }

        console.log(`@@@@@@@@ [Map] setOverlapFeatures() - 새 레이어 ${mapid} 생성 중`);
        const overlapLayer = createClusterLayer(name) as ClusterLayer;
        const overlapSource = overlapLayer.get("source") as ClusterSource;
        const vectorSource = overlapSource.getSource() as VectorSource;

        if (!overlapLayer || !overlapSource) {
            console.log("@@@@@@@@ [Map] setOverlapFeatures() - 레이어 생성 실패");
            return;
        }

        overlapLayer.setProperties({ mapid });
        console.log(`@@@@@@@@ [Map] setOverlapFeatures() - 레이어 속성 설정 완료: ${mapid}`);

        features.forEach((feature) => {
            //console.log("    @@ feature: ", feature);
            const categoryName = feature.get("category");
            const xid = (feature.getId() as string).split(".")[1];
            const option = mapInfo?.category ? findCategoryOption(mapInfo.category, categoryName) : null;

            const famous = mapInfo.famousMarker?.filter((item) => item.XID === Number(xid))[0];

            // 251127 | smyun | 카테고리 없는 지도를 오버랩 했을 때 처리 추가
            feature.setProperties({
                icon: option?.icon ?? metadata.marker ?? "marker",
                color: option?.color ?? metadata.markerColor ?? Color.BASIC_MARKER,
                famousImg: famous ? famous.url : undefined,
            });
        });

        // 벡터 소스에 피처 추가
        console.log(`@@@@@@@@ [Map] setOverlapFeatures() - 피처 ${features.length}개를 벡터 소스에 추가`);
        vectorSource.addFeatures(features);

        overlapLayer.setStyle(clusterStyleFunction);
        overlapLayer.setZIndex(1);
        overlapLayer.setVisible(true);

        // 새로 구성한 overlapLayer를 overlapLayerGroup에 추가
        const layers = overlapLayerGroup.getLayers();
        layers.push(overlapLayer);
        overlapLayerGroup.setVisible(true);

        console.log(`@@@@@@@@ [Map] setOverlapFeatures() - 레이어 ${mapid} 추가 완료`);
        console.log(`     >>> 오버랩 그룹 총 레이어 수: ${overlapLayerGroup.getLayersArray().length}`);
        console.log(`     >>> 현재 레이어의 피처 수: ${vectorSource.getFeatures().length}`);

        // 모든 레이어의 상태 확인
        overlapLayerGroup.getLayersArray().forEach((layer, idx) => {
            const layerMapId = layer.get("mapid");
            const layerSource = (layer.get("source") as ClusterSource)?.getSource() as VectorSource;
            const featureCount = layerSource ? layerSource.getFeatures().length : 0;
            console.log(
                `     >>> 레이어 ${idx}: mapid=${layerMapId}, 피처수=${featureCount}, visible=${layer.getVisible()}, opacity=${layer.getOpacity()}`
            );
        });

        // 지도 verctorSource 기준으로 fit extend 동작
        // const newExtent = vectorSource.getExtent();
        // if (
        //     !isNaN(newExtent[0]) &&
        //     !isNaN(newExtent[1]) &&
        //     !isNaN(newExtent[2]) &&
        //     !isNaN(newExtent[3]) &&
        //     newExtent[0] < newExtent[2] &&
        //     newExtent[1] < newExtent[3]
        // ) {
        //     fitToExtent(map, newExtent);
        // }

        setClusterInteractions(map, overlapLayer); // 클러스터의 Interactions 설정

        // 줌 레벨에 따라 표시 레이어 변경 ============================================
        const toggleLayersByZoom = () => {
            // console.log("@@@@@ toggleLayersByZoom @@@@@");

            const zoom = map.getView().getZoom() ?? 0;
            const currentClusterSource = overlapLayer.get("source") as ClusterSource; // 현재 클러스터 소스 참조

            // featureLayer.setVisible(false); // 항상 포인트 레이어는 숨김
            overlapLayer.setVisible(true); // 항상 클러스터 레이어는 표시

            if (zoom <= 5) {
                currentClusterSource.setDistance(200); // 대한민국 전체 범위 보기
            } else if (zoom <= 11) {
                currentClusterSource.setDistance(150); // 도/시 단위
            } else {
                currentClusterSource.setDistance(100); // 구/동 단위
            }

            // 25.07.02 | Kim Young Jun | 현재 선택된 피처 메모
            const currentSelectedIdx = selectedPoiIdxRef.current;

            // 25.07.02 | Kim Young Jun | 선택된 feature 하이라이트 유지
            // 25.08.27 | Kim Young Jun | 편집 상태 체크 및 위치 복원 추가
            if (currentSelectedIdx) {
                const feature = vectorSource.getFeatureById(`${mapid}.${currentSelectedIdx}`);
                // feature && selectFeature(feature);
                feature && highlightManager.selectSingleMarker(feature);

                // 편집 중인 마커의 위치 복원
                if (feature && highlightManager.isEditingFeature(`${mapid}.${currentSelectedIdx}`)) {
                    const tempCoords = highlightManager.getTemporaryCoordinates(`${mapid}.${currentSelectedIdx}`);
                    if (tempCoords) {
                        const geometry = feature.getGeometry();
                        if (geometry instanceof Point) {
                            geometry.setCoordinates(tempCoords);
                        }
                    }
                }
            }

            // 25.09.12 | Kim Young Jun | 지도 전환 중 대기 중인 하이라이트 복원
            if (pendingHighlightRef.current && pendingHighlightRef.current.mapId === mapid) {
                const pendingXid = pendingHighlightRef.current.xid;
                const pendingFeature = vectorSource.getFeatureById(`${mapid}.${pendingXid}`);

                if (pendingFeature) {
                    // 하이라이트 적용
                    // pendingFeature.set("selected", true);
                    pendingFeature.setProperties({ selected: true });

                    highlightManager.selectSingleMarker(pendingFeature);

                    // selectedPoiIdx도 업데이트
                    props.setSelectedPoiIdx(pendingXid);
                }

                // 복원 완료 후 대기 정보 클리어
                pendingHighlightRef.current = null;
            }
        };

        // 초기 체크
        toggleLayersByZoom();

        const excludedTypes = [MapToolType.Region, MapToolType.Choropleth, MapToolType.Chart, MapToolType.Bubble, MapToolType.Heat];

        map.getView().on("change:resolution", () => {
            syncing = true;
            if (!excludedTypes.includes(activeIdxRef.current)) {
                toggleLayersByZoom();
            }
            setTimeout(() => {
                syncing = false;
            }, 500); // 500ms 동안 수동 줌 상태 유지
        });

        // 초기 알림(Toast) 출력
        if (!initialOverlapToastShown.current.has(mapid)) {
            props.setToast &&
                props.setToast({
                    type: ToastType.DEFAULT,
                    message: "새로운 지도가 오버랩 되었습니다.",
                    iconType: "addTask",
                });
            initialOverlapToastShown.current.add(mapid);
        }
    };

    /**
     * 신규 장소 일괄 추가 - 장소 미리보기
     * @param map {@link OlMap}
     */
    const setNewFeatures = (map: OlMap) => {
        const addLayer = getLayer(map, LayerName.ADD) as Layer;
        const features: OlFeature[] = [];
        const vectorSource = new VectorSource({});
        const coordsListArr = props.newPlaces;

        if (!addLayer) return;

        if (coordsListArr && coordsListArr.length > 0) {
            coordsListArr.forEach((list, fileIdx) => {
                Object.keys(list).map((rowNum) => {
                    const { coords, ...properties } = list[Number(rowNum)];
                    const feature = new OlFeature(new Point(coords));
                    const style = addPointStyle.clone();
                    style.setText(null as any);
                    feature.setStyle(style);

                    const option = findCategoryOption(props.categories, properties.category);

                    feature.setProperties({
                        xid: `${fileIdx}_${rowNum}`,
                        icon: option?.icon ?? "marker",
                        color: option?.color ?? "default",
                        isNew: true,
                        ...properties,
                    });

                    features.push(feature);
                });
            });

            vectorSource.addFeatures(features);

            const newExtent = vectorSource.getExtent();
            fitToExtent(map, newExtent);
            addLayer.setSource(vectorSource);
            addLayer.setVisible(true);
        } else {
            addLayer.setSource(vectorSource);
        }
    };

    /**
     * 신규 장소 추가 - 지도에서 위치 선택
     * @param map {@link OlMap}
     */
    const toggleAddMode = (map: OlMap) => {
        const layer = getLayer(map, LayerName.ADD);
        const center = map.getView().getCenter();
        const source = layer?.getSource() as VectorSource;

        if (!layer || !source || !center) return;

        const translate = getInteraction(map, LayerName.ADD);
        translate && map.removeInteraction(translate);

        if (props.isAddMode && props.sideSheetCont === ContentList.AddSinglePlace) {
            source.clear();

            const feature = new OlFeature(new Point(center));
            feature.setStyle(addPointStyle);
            source.addFeature(feature);

            const translate = new Translate({
                features: new Collection([feature]),
            });

            translate.set("name", LayerName.ADD);
            translate.set("layers", [layer]);
            translate.on("translatestart", onTranslateStart);
            translate.on("translateend", onTranslateEnd);
            translate.on("translating", onTranslating);

            layer.setVisible(true);
            map.addInteraction(translate);

            saveCoords(center);
        } else {
            const layer = getLayer(map, LayerName.ADD);
            layer?.setVisible(false);
        }
    };
    
    // 2026.01.15 | dslee | ref로 외부에 노출할 함수들
    useImperativeHandle(ref, () => ({

        // 드래그 가능한 마커 생성
        showDraggableMarker: () => {
            if (mapObj) {
                const layer = getLayer(mapObj, LayerName.ADD) as Layer;
                const source = layer?.getSource() as VectorSource;
                if (!layer || !source) return;
                
                const center = mapObj.getView().getCenter();
                if (!center) return;
                
                source.clear();
                const translate = getInteraction(mapObj, LayerName.ADD);
                translate && mapObj.removeInteraction(translate);
                
                const feature = new OlFeature(new Point(center));
                feature.setStyle(addPointStyle);
                source.addFeature(feature);
                
                const newTranslate = new Translate({
                    features: new Collection([feature]),
                });
                
                newTranslate.set("name", LayerName.ADD);
                newTranslate.set("layers", [layer]);
                newTranslate.on("translatestart", onTranslateStart);
                newTranslate.on("translateend", (e: TranslateEvent) => {
                    const coordinates = (e.features.getArray()[0].getGeometry() as Point).getCoordinates();
                    props.onMarkerDragEnd?.(coordinates); // dslee (2026.1.13) : 웹뷰 전송
                });
                newTranslate.on("translating", onTranslating);
                
                layer.setVisible(true);
                mapObj.addInteraction(newTranslate);
            }
        },

        // 드래그 가능한 마커 비활성화
        hideDraggableMarker: () => {
            if (mapObj) {
                const layer = getLayer(mapObj, LayerName.ADD);
                layer?.setVisible(false);
                const translate = getInteraction(mapObj, LayerName.ADD);
                translate && mapObj.removeInteraction(translate);
            }
        },

        refreshMap: () => {
            if (!mapObj || !baseMapID) return;
            reqMapInfoData(baseMapID); // 여기서 지도 정보 새로고침
            reqMapData(baseMapID, mapObj, "map", false); //  마커 데이터 새로고침, doFit 은 False(줌아웃 안함)
        },

        centerToLocation: (latitude: number, longitude: number) => {
            if (mapObj) {
                const projection = mapObj.getView().getProjection();
                const projectionCode = projection.getCode();
                const coords = transform([longitude, latitude], "EPSG:4326", projectionCode); // EPSG:4326 → EPSG:5179 변환
                
                mapObj.getView().animate({
                    center: coords,
                    zoom: 10,
                    duration: 500,
                });
            }
        }

    }), [mapObj, baseMapID, onTranslateStart, onTranslateEnd, onTranslating]);

    const toggleEditMode = (map: OlMap) => {
        const layer = getLayer(map, LayerName.EDIT);
        const center = map.getView().getCenter();
        const source = layer?.getSource() as VectorSource;

        if (!layer || !source || !center) return;

        source.clear();

        const translate = getInteraction(map, LayerName.EDIT);
        translate && map.removeInteraction(translate);

        if (props.isAddMode && props.sideSheetCont === ContentList.EditPlace) {
            // 편집 모드 활성화
            highlightManager.setEditMode(true);

            const translate = new Translate({
                filter: (feature: OlFeature) => {
                    // 편집 모드에서 클러스터 이동 제어
                    const canTranslate = highlightManager.canTranslateInEditMode(feature, `${baseMapID}.${selectedPoiIdxRef.current}`);

                    if (!canTranslate) {
                        return false;
                    }

                    const features = feature.get("features");
                    const selectedFeature = features?.find((f: OlFeature) => {
                        const featureId = f.getId();
                        if (featureId === `${baseMapID}.${selectedPoiIdxRef.current}`) {
                            return true;
                        }
                    });

                    if (selectedFeature) {
                        return true;
                    }
                    return false;

                    //return (feature as any).values_.features[0].id_ === `${baseMapID}.${selectedIdxRef.current}`;
                },
            });

            translate.set("name", LayerName.EDIT);
            translate.set("layers", [layer]);
            translate.on("translatestart", onTranslateStart);
            translate.on("translating", onTranslating);
            translate.on("translateend", onTranslateEnd);

            layer.setVisible(true);
            map.addInteraction(translate);

            saveCoords(center);
        } else {
            // 편집 모드 비활성화
            highlightManager.setEditMode(false);
            // 편집 중인 상태도 정리 (취소가 아닌 일반적인 모드 종료)
            highlightManager.finishEditing();

            const layer = getLayer(map, LayerName.EDIT);
            layer?.setVisible(false);
        }
    };

    // @@ 분석 기능 추가
    const setHeatBlurByMetric = async (map: OlMap) => {
        const layer = getLayer(map, LayerName.HEAT) as HeatmapLayer;
        const clusterLayer = getLayer(map, LayerName.CLUSTER) as ClusterLayer;
        const clusterSource = clusterLayer.get("source") as ClusterSource;
        const vectorSource = clusterSource.getSource() as VectorSource;

        if (!layer || !clusterLayer || !clusterSource || !vectorSource) return;

        const features = vectorSource.getFeatures();
        const weights: number[] = [];

        // metric 기준값 추출
        // 루프 밖으로 고정값 hoist
        const idx = props.analysisMetricsToolIdx;
        const selectedField = props.selectedDropdownField ?? null;

        // 문자열 파싱 헬퍼 (split 대신 slice)
        const getXid = (id: string): number => {
            const dot = id.indexOf(".");
            return dot >= 0 ? +id.slice(dot + 1) : NaN;
        };

        // metric 값 함수도 루프 외에 클로저 캡처 최소화 & 분기 단순화
        const getMetricValue = (xid: number): number => {
            const placeInfoSet = allPlaceDataRef.current[xid];
            if (!placeInfoSet) return 0;

            if (idx === 21) return 1;
            if (idx === 22) return placeInfoSet.primaryinfo?.thumbCnt ?? 0;
            if (idx === 23) return placeInfoSet.primaryinfo?.viewCnt ?? 0;

            if (selectedField && idx && idx >= 25) {
                const val = placeInfoSet.placeInfo[selectedField];
                if (val == null) return 0;
                if (typeof val === "number") return val;
                if (typeof val === "string") {
                    // 쉼표 제거: 정규식 대신 단순 루프/replaceAll(크롬 최신) 권장
                    const num = Number(val.replaceAll(",", ""));
                    return Number.isNaN(num) ? 0 : num;
                }
            }
            return 0;
        };

        const SILENT = true;
        const N = features.length;
        weights.length = 0; // 재사용 시 초기화

        for (let i = 0; i < N; i++) {
            // console.log("1차수집");
            const f = features[i];
            const xid = getXid(f.getId() as string);
            const value = getMetricValue(xid);
            // 이벤트/리렌더 방지
            f.set("weight_raw", value, SILENT);
            weights.push(value);
        }

        if (idx !== 21) {
            // 1) max 계산: ...spread 대신 O(n) 단순 루프 (메모리/속도 안정)
            let max = 1; // 0으로 나눔 방지
            for (let i = 0; i < weights.length; i++) {
                const w = weights[i];
                if (w > max) max = w;
            }

            // 2) 로그 밑 미리 계산 (반복에서 Math.log 호출 제거)
            //    log(raw+1) / log(max+1)  == Math.log1p(raw) * (1 / Math.log1p(max))
            const invLog = 1 / Math.log1p(max);

            // 3) 루프: forEach 대신 for, console.log 제거, set은 silent로 이벤트 억제
            for (let i = 0; i < features.length; i++) {
                const f = features[i];
                const raw = (f.get("weight_raw") as number) || 0;
                const normalized = Math.log1p(raw) * invLog;

                // 값이 동일하면 set 생략 (렌더/이벤트 줄이기)
                if (f.get("weight") !== normalized) {
                    (f as any).set("weight", normalized, SILENT);
                }
            }
        } else {
            // idx === 21: 전부 1로 고정
            for (let i = 0; i < features.length; i++) {
                const f = features[i];
                if (f.get("weight") !== 1) {
                    (f as any).set("weight", 1, SILENT);
                }
            }
        }

        // HeatmapLayer 적용
        vectorSource.changed();
        layer.setSource(vectorSource);
        layer.setBlur(props.blur);

        switchLayer(map, layer);
    };

    const switchLayer = (map: OlMap, layer: BaseLayer) => {
        const featureLayer = getLayer(map, LayerName.CLUSTER);
        featureLayer?.setVisible(false);

        map.getLayers().forEach((l) => {
            const name = l.get("name") as LayerName;
            if (![LayerName.FEATURE, LayerName.CLUSTER, LayerName.BASE].includes(name)) {
                l.setVisible(false);
            }
        });

        setIsOnGraph(false);
        layer?.setVisible(true); // 레이어 보이기
    };

    // 기존 unsetAllFeatures 함수는 MarkerHighlightManager로 대체됨
    // const unsetAllFeatures = (layer: ClusterLayer | VectorLayer<VectorSource>, name: string) => {
    //     const source = (layer instanceof ClusterLayer ? (layer.get("source") as ClusterSource).getSource() : layer.getSource()) as VectorSource;
    //     source?.getFeatures().forEach((f) => f.unset(name));
    // };

    return (
        <>
            <ModalComponent />
            <div className={styles.wrapper}>
                <div id={styles.map} ref={mapRef}></div>

                <div
                    ref={graphOverlayRef}
                    style={{
                        display: isOnGraph ? "block" : "none",
                        width: "280px",
                        height: "240px",
                        position: "relative",
                        padding: "15px",
                        overflow: "hidden",
                        zIndex: 9999,
                    }}
                />

                <div id={styles.remove} ref={removeRef}></div>
            </div>
        </>
    );
});

export default Map;
